import React, { useState, useEffect, useRef } from 'react'
import {
  BookOpen,
  Globe,
  FileText,
  Search,
  CheckCircle,
  AlertCircle,
  Loader,
  Plus,
  X,
  Menu,
  TrendingUp,
  RefreshCw,
  Download,
  Upload,
  Clock,
  Users,
  Zap,
  Shield,
  File,
  Copy,
  Check,
  Database
} from 'lucide-react'
import { useTenant } from '../contexts/TenantContext'
import { CAIQ_V4, TOTAL_QUESTIONS, CAIQ_VERSION } from '../data/caiq-v4-template'
import { featureFlags } from '../lib/featureFlags'
import VendorRiskAssessment from '../components/VendorRiskAssessment'
import ContractsManagement from './ContractsManagement'
import ContractManagementDashboard from '../components/ContractManagementDashboard'
import VendorRiskManagementDashboard from '../components/VendorRiskManagementDashboard'
import TPRMDashboard from '../components/TPRMDashboard'
import ReactMarkdown from 'react-markdown'

interface ScanResults {
  answers: any[]
  conflicts: any[]
  scannedAt: string
  sources: string[]
  documentCount: number
}

interface KnowledgeBasePageProps {
  onNavigateToSettings?: () => void
  initialActiveTab?: 'query' | 'questionnaire' | 'caiq' | 'tprm' | 'vendor-risk'
  initialCaiqSubTab?: 'configure' | 'scan' | 'review' | 'browse' | 'analytics'
}

const KnowledgeBasePage: React.FC<KnowledgeBasePageProps> = ({
  onNavigateToSettings,
  initialActiveTab,
  initialCaiqSubTab
}) => {
  const { currentTenant } = useTenant()
  const [adminEmail, setAdminEmail] = useState<string>('Admin')
  const [activeTab, setActiveTab] = useState<'query' | 'questionnaire' | 'caiq' | 'tprm' | 'vendor-risk'>(initialActiveTab || 'query')
  const [caiqSubTab, setCAIQSubTab] = useState<'configure' | 'scan' | 'review' | 'browse' | 'analytics'>(initialCaiqSubTab || 'configure')
  const [tprmSubTab, setTprmSubTab] = useState<'dashboard' | 'assessments' | 'vendors' | 'insights' | 'processing'>('dashboard')

  // Update tabs when props change (for tour navigation)
  useEffect(() => {
    if (initialActiveTab) {
      setActiveTab(initialActiveTab)
    }
  }, [initialActiveTab])

  useEffect(() => {
    if (initialCaiqSubTab) {
      setCAIQSubTab(initialCaiqSubTab)
    }
  }, [initialCaiqSubTab])
  const [subMenuOpen, setSubMenuOpen] = useState(false)
  const [sources, setSources] = useState<Array<{
    url: string
    addedAt: string
    lastScanned?: string
    answersFound?: number
    status: 'active' | 'testing' | 'error'
    errorMessage?: string
  }>>([])
  const [newSource, setNewSource] = useState('')
  const [testingUrl, setTestingUrl] = useState<string | null>(null)
  const [scanDepth, setScanDepth] = useState<number>(5)
  const [scanning, setScanning] = useState(false)
  const [scanProgress, setScanProgress] = useState('')
  const [scanResults, setScanResults] = useState<ScanResults | null>(null)
  const [completion, setCompletion] = useState(0)
  const [documents, setDocuments] = useState<any[]>([])
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([])
  const [loadingDocs, setLoadingDocs] = useState(true)
  const [discovering, setDiscovering] = useState(false)
  const [discoveredUrls, setDiscoveredUrls] = useState<any[]>([])
  const [companyDomain, setCompanyDomain] = useState('')
  const [showDiscoveryModal, setShowDiscoveryModal] = useState(false)
  const [editingAnswer, setEditingAnswer] = useState<any | null>(null)
  const [confidenceFilter, setConfidenceFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all')
  const [showConflictsOnly, setShowConflictsOnly] = useState(false)
  const [savedAnswers, setSavedAnswers] = useState<Record<string, any>>({})
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set())
  const [loadingKb, setLoadingKb] = useState(false)
  const [manualEntryQuestion, setManualEntryQuestion] = useState<any | null>(null)
  const [manualAnswer, setManualAnswer] = useState({
    answer: 'yes' as 'yes' | 'no' | 'unclear' | 'text',
    explanation: '',
    confidence: 1.0,
    sources: [] as any[]
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDomain, setSelectedDomain] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'answered' | 'unanswered'>('all')
  const [reviewStatusFilter, setReviewStatusFilter] = useState<'all' | 'draft' | 'ai_draft' | 'under_review' | 'approved' | 'needs_update'>('all')
  const [showMappings, setShowMappings] = useState(false)
  const [selectedAnswers, setSelectedAnswers] = useState<Set<string>>(new Set())
  const [showImportModal, setShowImportModal] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [partialScanExists, setPartialScanExists] = useState(false)
  const [partialScanProgress, setPartialScanProgress] = useState<{questionsProcessed: number, totalQuestions: number, nextStartIndex: number} | null>(null)
  const [importPreview, setImportPreview] = useState<any[]>([])
  const [importStrategy, setImportStrategy] = useState<'replace' | 'merge' | 'update'>('merge')
  const [importing, setImporting] = useState(false)
  const [systemSettings, setSystemSettings] = useState<any>(null)
  const [hasApiKey, setHasApiKey] = useState(false)
  const [apiKeySource, setApiKeySource] = useState<'tenant' | 'platform' | 'none'>('none')
  const [userRole, setUserRole] = useState<'admin' | 'editor' | 'reviewer' | 'viewer'>('admin')
  const [activityLogs, setActivityLogs] = useState<any[]>([])
  const [showActivityLog, setShowActivityLog] = useState(false)
  const [editLock, setEditLock] = useState<any | null>(null)
  const [lockError, setLockError] = useState<string | null>(null)
  // AI Response Generation
  const [selectedQuestionsForGen, setSelectedQuestionsForGen] = useState<Set<string>>(new Set())
  const [generatedResponses, setGeneratedResponses] = useState<any[]>([])
  const [generating, setGenerating] = useState(false)
  const [showGeneratedReview, setShowGeneratedReview] = useState(false)
  const [selectedGeneratedForApproval, setSelectedGeneratedForApproval] = useState<Set<number>>(new Set())

  // Query Tab
  const [queryText, setQueryText] = useState('')
  const [queryResults, setQueryResults] = useState<any[]>([])
  const [querying, setQuerying] = useState(false)
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)

  // Questionnaire Tab
  const [questionnaireFile, setQuestionnaireFile] = useState<File | null>(null)
  const [questionnaireQuestions, setQuestionnaireQuestions] = useState<any[]>([])
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState<any[]>([])
  const [processingQuestionnaire, setProcessingQuestionnaire] = useState(false)
  const [questionnaireUnknowns, setQuestionnaireUnknowns] = useState<any[]>([])
  const [showGeneratePrompt, setShowGeneratePrompt] = useState(false)
  const [selectedResponseType, setSelectedResponseType] = useState<'positive' | 'negative'>('positive')
  const [selectedQuestions, setSelectedQuestions] = useState<Set<number>>(new Set())
  const [generatingQuestions, setGeneratingQuestions] = useState(false)
  const [preservedQuestionnaires, setPreservedQuestionnaires] = useState<any[]>([])
  const [loadingPreservedQuestionnaires, setLoadingPreservedQuestionnaires] = useState(false)

  // SECURITY FIX: Track all active polling intervals to prevent memory leaks and state updates after unmount
  const pollIntervalsRef = useRef<Set<NodeJS.Timeout>>(new Set())

  // Helper function to clear all polling intervals
  const clearPollingIntervals = () => {
    pollIntervalsRef.current.forEach(intervalId => clearInterval(intervalId))
    pollIntervalsRef.current.clear()
  }

  // Helper function to create tracked interval
  const createTrackedInterval = (callback: () => void, delay: number): NodeJS.Timeout => {
    const intervalId = setInterval(callback, delay)
    pollIntervalsRef.current.add(intervalId)
    return intervalId
  }

  // Cleanup all polling intervals on unmount
  useEffect(() => {
    return () => {
      clearPollingIntervals()
    }
  }, [])

  useEffect(() => {
    // Load admin email from session
    try {
      const session = localStorage.getItem('admin-session')
      if (session) {
        const { email } = JSON.parse(session)
        if (email) {
          setAdminEmail(email)
        }
      }
    } catch (e) {
      console.error('Failed to load admin email:', e)
    }
  }, [])

  useEffect(() => {
    if (currentTenant) {
      // Load saved sources
      loadSources()
      // Load uploaded documents
      loadDocuments()
      // Load saved knowledge base (also calculates completion)
      loadSavedKnowledgeBase()
      // Load pending scan results for review
      loadScanResults()
      // Load system settings to check for API key
      loadSystemSettings()
      // Load user role
      loadUserRole()
      // Load activity logs
      loadActivityLogs()
      // Load preserved questionnaires
      loadPreservedQuestionnaires()
    }
  }, [currentTenant])

  // Clear selections when filters change
  useEffect(() => {
    setSelectedAnswers(new Set())
  }, [confidenceFilter, showConflictsOnly])

  const loadSources = async () => {
    // In production, load from Blobs
    const defaultSources = [
      `https://${currentTenant?.domain || 'company.com'}/security`,
      `https://${currentTenant?.domain || 'company.com'}/trust`,
      `https://${currentTenant?.domain || 'company.com'}/privacy`
    ].map(url => ({
      url,
      addedAt: new Date().toISOString(),
      status: 'active' as const
    }))
    setSources(defaultSources)
  }

  const testUrl = async (url: string) => {
    setTestingUrl(url)

    try {
      const response = await fetch(url, { method: 'HEAD', mode: 'no-cors' })

      // Update source status
      setSources(prev => prev.map(s =>
        s.url === url
          ? { ...s, status: 'active' as const, errorMessage: undefined }
          : s
      ))

      return true
    } catch (error) {
      // Update source with error
      setSources(prev => prev.map(s =>
        s.url === url
          ? { ...s, status: 'error' as const, errorMessage: 'URL not accessible' }
          : s
      ))

      return false
    } finally {
      setTestingUrl(null)
    }
  }

  const loadDocuments = async () => {
    if (!currentTenant) return

    setLoadingDocs(true)
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/admin/documents-db', {
        credentials: 'include',
        headers
      })
      if (response.ok) {
        const docs = await response.json()
        // Filter out deleted and archived documents
        const activeDocuments = docs.filter((d: any) => !d.deleted && !d.archived)
        setDocuments(activeDocuments)
        // Auto-select all active documents by default
        setSelectedDocIds(activeDocuments.map((d: any) => d.id))
      }
    } catch (error) {
      console.error('Error loading documents:', error)
    } finally {
      setLoadingDocs(false)
    }
  }

  const loadSavedKnowledgeBase = async (forceReload = false) => {
    if (!currentTenant) return

    setLoadingKb(true)
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
      if (forceReload) {
        headers['Cache-Control'] = 'no-cache'
      }

      // Add cache busting parameter when force reloading
      const cacheBuster = forceReload ? `&_t=${Date.now()}` : ''
      const response = await fetch(`/api/get-kb?tenantId=${currentTenant.id}${cacheBuster}`, {
        cache: forceReload ? 'no-cache' : 'default',
        credentials: 'include',
        headers
      })
      if (response.ok) {
        const data = await response.json()
        console.log('Loaded KB answers:', Object.keys(data.answers || {}).length)
        if (data.answers) {
          setSavedAnswers(data.answers)
          // Calculate completion (rounded to 1 decimal place)
          const answeredCount = Object.keys(data.answers).length
          setCompletion(Math.round((answeredCount / TOTAL_QUESTIONS) * 1000) / 10)
        }
      }
    } catch (error) {
      console.error('Error loading knowledge base:', error)
    } finally {
      setLoadingKb(false)
    }
  }

  const loadSystemSettings = async () => {
    try {
      // Add Authorization header from localStorage
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      }
      const token = localStorage.getItem('auth-token')
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/settings-db', {
        credentials: 'include',
        headers
      })
      if (response.ok) {
        const data = await response.json()
        const settings = data.success ? data.result : data
        setSystemSettings(settings)

        // SECURITY: API keys are no longer returned in settings (server-side only)
        // Check tenant secrets endpoint to see if key exists
        try {
          const secretsResponse = await fetch('/api/admin/tenant-secrets', {
            credentials: 'include',
            headers
          })
          if (secretsResponse.ok) {
            const secretsData = await secretsResponse.json()
            console.log('🔍 [KnowledgeBase] tenant-secrets response:', secretsData)
            console.log('🔍 [KnowledgeBase] sources.claudeApiKey:', secretsData.sources?.claudeApiKey)
            console.log('🔍 [KnowledgeBase] metadata.claudeApiKey:', secretsData.metadata?.claudeApiKey)
            const hasKey = secretsData.metadata?.claudeApiKey || secretsData.sources?.claudeApiKey === 'platform'
            const source = secretsData.sources?.claudeApiKey || 'none'
            console.log('🔍 [KnowledgeBase] Detected hasKey:', hasKey, 'source:', source)
            setHasApiKey(hasKey)
            setApiKeySource(source)
          }
        } catch (secretsError) {
          console.error('Error checking API key status:', secretsError)
          setHasApiKey(false)
          setApiKeySource('none')
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error)
    }
  }

  const loadUserRole = async () => {
    if (!currentTenant) return

    try {
      const authToken = localStorage.getItem('auth-token')
      if (!authToken) return

      // Backend gets user email from JWT token, no need to pass it
      const response = await fetch(`/api/kb-user-roles?tenantId=${currentTenant.id}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUserRole(data.role?.role || 'admin')
      }
    } catch (error) {
      console.error('Error loading user role:', error)
      setUserRole('admin') // Default to admin on error
    }
  }

  const loadActivityLogs = async () => {
    if (!currentTenant) return

    try {
      const authToken = localStorage.getItem('auth-token')
      const response = await fetch(`/api/kb-audit-log?tenantId=${currentTenant.id}&limit=20`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${authToken || ''}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setActivityLogs(data.logs || [])
      }
    } catch (error) {
      console.error('Error loading activity logs:', error)
    }
  }

  const hasPermission = (action: 'scan' | 'edit' | 'approve') => {
    const permissions: Record<string, string[]> = {
      admin: ['scan', 'edit', 'approve'],
      editor: ['scan', 'edit'],
      reviewer: ['approve'],
      viewer: []
    }
    return permissions[userRole]?.includes(action) || false
  }

  const toggleQuestion = (questionId: string) => {
    setExpandedQuestions(prev => {
      const next = new Set(prev)
      if (next.has(questionId)) {
        next.delete(questionId)
      } else {
        next.add(questionId)
      }
      return next
    })
  }

  const toggleDocument = (docId: string) => {
    setSelectedDocIds(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    )
  }

  const toggleAllDocuments = () => {
    if (selectedDocIds.length === documents.length) {
      setSelectedDocIds([])
    } else {
      setSelectedDocIds(documents.map(d => d.id))
    }
  }

  const addSource = async () => {
    if (newSource && !sources.find(s => s.url === newSource)) {
      const newSourceObj = {
        url: newSource,
        addedAt: new Date().toISOString(),
        status: 'testing' as const
      }
      setSources([...sources, newSourceObj])
      setNewSource('')

      // Test the URL
      await testUrl(newSource)
    }
  }

  const removeSource = (url: string) => {
    setSources(sources.filter(s => s.url !== url))
  }

  const discoverUrls = async () => {
    if (!companyDomain) {
      alert('Please enter a company domain (e.g., company.com)')
      return
    }

    setDiscovering(true)
    try {
      const response = await fetch('/api/discover-security-urls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyDomain })
      })

      if (response.ok) {
        const result = await response.json()
        setDiscoveredUrls(result.discovered || [])
        setShowDiscoveryModal(true)
      } else {
        const error = await response.json()
        alert(error.error || 'Failed to discover URLs')
      }
    } catch (error) {
      console.error('Error discovering URLs:', error)
      alert('Failed to discover URLs. Please check the domain and try again.')
    } finally {
      setDiscovering(false)
    }
  }

  const addDiscoveredUrls = (urls: string[]) => {
    const newSources = [...sources]
    const now = new Date().toISOString()
    urls.forEach(url => {
      if (!newSources.find(s => s.url === url)) {
        newSources.push({
          url,
          addedAt: now,
          status: 'active' as const
        })
      }
    })
    setSources(newSources)
    setShowDiscoveryModal(false)
    setDiscoveredUrls([])
  }

  const clearProgress = async () => {
    if (!currentTenant) return

    if (!confirm('Are you sure you want to clear all scan progress? This will delete all existing answers and you will need to start over.')) {
      return
    }

    try {
      // Clear from blob store
      await fetch(`/api/clear-kb-scan-results?tenantId=${currentTenant.id}`, {
        method: 'DELETE',
        credentials: 'include'
      })

      // Clear local state
      setScanResults(null)
      setPartialScanExists(false)
      setPartialScanProgress(null)
      setScanProgress('')

      // Refresh activity logs
      await loadActivityLogs()

      alert('Scan progress cleared successfully')
    } catch (error) {
      console.error('Error clearing progress:', error)
      alert('Failed to clear progress')
    }
  }

  const startScan = async (startIndex?: number, forceRestart = false) => {
    if (!currentTenant) return

    // Auto-resume if partial scan exists and not forcing restart
    let actualStartIndex = startIndex !== undefined ? startIndex : 0
    if (!forceRestart && partialScanProgress && actualStartIndex === 0) {
      actualStartIndex = partialScanProgress.nextStartIndex
      console.log(`Auto-resuming scan from question ${actualStartIndex + 1}`)
    }

    setScanning(true)
    setScanProgress(actualStartIndex === 0 ? 'Starting scan...' : `Continuing scan from question ${actualStartIndex + 1}...`)

    // Log what we're about to send
    console.log('🚀 Starting KB scan with:', {
      tenantId: currentTenant.id,
      documentIds: selectedDocIds,
      documentCount: selectedDocIds.length,
      webUrls: sources.map(s => s.url),
      scanDepth,
      startIndex: actualStartIndex
    })

    try {
      // Fire and forget - don't wait for response (avoids browser timeout)
      fetch('/api/scan-knowledge-sources', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: currentTenant.id,
          sources: {
            webUrls: sources.map(s => s.url),
            documentIds: selectedDocIds,
            scanDepth
          },
          startIndex: actualStartIndex
        })
      }).then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
          console.error('❌ Scan request failed:', {
            status: response.status,
            statusText: response.statusText,
            error: errorData.error,
            details: errorData.details,
            tenantId: currentTenant.id
          })
        }
      }).catch(err => console.error('Scan request error:', err, {
        message: err?.message,
        stack: err?.stack,
        tenantId: currentTenant.id
      }))

      // Start polling for status
      pollContinuousScan()
    } catch (error) {
      console.error('Scan error:', error)
      setScanProgress('Scan failed')
      setScanning(false)
    }
  }

  const pollContinuousScan = async () => {
    if (!currentTenant) return

    // Clear any existing polling intervals before starting new one
    clearPollingIntervals()

    let lastProcessedIndex = -1 // Track which batch we've triggered

    const pollInterval = createTrackedInterval(async () => {
      try {
        const response = await fetch(`/api/check-scan-status?tenantId=${currentTenant.id}`)
        if (!response.ok) return

        const status = await response.json()

        if (status.status === 'partial') {
          // Scan batch completed, more to process
          setScanProgress(`Processed ${status.results?.found || 0} answers... (${status.progress}%)`)

          // Trigger next batch only if we haven't already triggered it
          const nextIndex = status.nextStartIndex
          if (nextIndex !== null && nextIndex !== undefined && nextIndex !== lastProcessedIndex) {
            lastProcessedIndex = nextIndex
            console.log(`Triggering next batch starting at question ${nextIndex + 1}`)

            fetch('/api/scan-knowledge-sources', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                tenantId: currentTenant.id,
                sources: {
                  webUrls: sources.map(s => s.url),
                  documentIds: selectedDocIds,
                  scanDepth
                },
                startIndex: nextIndex
              })
            }).catch(err => console.log('Continuing scan, ignoring timeout:', err.message))
          }
        } else if (status.status === 'completed') {
          // All done!
          pollIntervalsRef.current.delete(pollInterval)
          clearInterval(pollInterval)
          setScanProgress(`Scan completed! Found ${status.results?.found || 0} answers`)
          await loadScanResults()
          await loadActivityLogs() // Refresh activity log
          setActiveTab('review')
          setScanning(false)
        } else if (status.status === 'error') {
          pollIntervalsRef.current.delete(pollInterval)
          clearInterval(pollInterval)
          setScanProgress('Scan failed: ' + (status.message || 'Unknown error'))
          setScanning(false)
        }
      } catch (error) {
        console.error('Poll error:', error)
      }
    }, 5000) // Poll every 5 seconds (increased from 3 to reduce load)

    // Safety timeout - stop polling after 30 minutes
    setTimeout(() => {
      pollIntervalsRef.current.delete(pollInterval)
      clearInterval(pollInterval)
      setScanning(false)
    }, 30 * 60 * 1000)
  }

  const pollScanStatus = async () => {
    if (!currentTenant) return

    // Clear any existing polling intervals before starting new one
    clearPollingIntervals()

    const checkStatus = async (): Promise<boolean> => {
      try {
        const response = await fetch(`/api/check-scan-status?tenantId=${currentTenant.id}`)
        if (!response.ok) {
          throw new Error('Failed to check status')
        }

        const status = await response.json()

        if (status.status === 'running') {
          // Update progress
          const progress = status.progress || 0
          setScanProgress(`${status.message || 'Processing...'} (${progress}%)`)
          return false // Not done yet
        } else if (status.status === 'completed') {
          // Scan completed
          const results = status.results
          setScanProgress(`Scan completed! Found ${results?.found || 0} answers`)

          // Load scan results and show review tab
          await loadScanResults()
          await loadActivityLogs() // Refresh activity log
          setActiveTab('review')
          setScanning(false)
          return true // Done
        } else if (status.status === 'error') {
          // Scan failed
          setScanProgress(`Scan failed: ${status.message}`)
          setScanning(false)
          return true // Done (with error)
        }

        return false
      } catch (error) {
        console.error('Error checking scan status:', error)
        return false
      }
    }

    // Poll every 2 seconds until done
    const pollInterval = createTrackedInterval(async () => {
      const isDone = await checkStatus()
      if (isDone) {
        pollIntervalsRef.current.delete(pollInterval)
        clearInterval(pollInterval)
      }
    }, 2000)

    // Also check immediately
    const isDone = await checkStatus()
    if (isDone) {
      pollIntervalsRef.current.delete(pollInterval)
      clearInterval(pollInterval)
    }
  }

  const loadScanResults = async () => {
    if (!currentTenant) return

    try {
      const response = await fetch(`/api/get-kb-scan-results?tenantId=${currentTenant.id}`)
      if (response.ok) {
        const data = await response.json()
        if (data.found !== false) {
          setScanResults(data)

          // Check if this is a partial scan (incomplete)
          if (data.isComplete === false && data.nextStartIndex) {
            setPartialScanExists(true)
            setPartialScanProgress({
              questionsProcessed: data.questionsProcessed || 0,
              totalQuestions: data.totalQuestions || 261,
              nextStartIndex: data.nextStartIndex
            })
          } else {
            setPartialScanExists(false)
            setPartialScanProgress(null)
          }
        }
      }
    } catch (error) {
      console.error('Error loading scan results:', error)
    }
  }

  const approveAnswer = async (answer: any) => {
    if (!currentTenant) return

    try {
      const response = await fetch('/api/save-kb-answers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: currentTenant.id,
          answers: [answer],
          replaceAll: false
        })
      })

      if (response.ok) {
        // Remove from pending
        if (scanResults) {
          setScanResults({
            ...scanResults,
            answers: scanResults.answers.filter(a => a.questionId !== answer.questionId)
          })
        }
        // Reload KB and completion
        loadSavedKnowledgeBase()
      } else {
        // Consume response body and show error to user
        const errorText = await response.text()
        alert(`Failed to approve answer: ${errorText || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error saving answer:', error)
      // Show error to user instead of silently failing
      alert(`Error approving answer: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const approveAllHighConfidence = async () => {
    if (!currentTenant || !scanResults) return

    const highConfidenceAnswers = scanResults.answers.filter(a => a.confidence > 0.8)
    if (highConfidenceAnswers.length === 0) return

    try {
      const response = await fetch('/api/save-kb-answers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: currentTenant.id,
          answers: highConfidenceAnswers,
          replaceAll: false
        })
      })

      if (response.ok) {
        // Remove approved answers
        setScanResults({
          ...scanResults,
          answers: scanResults.answers.filter(a => a.confidence <= 0.8)
        })
        loadSavedKnowledgeBase()
      } else {
        // Consume response body and show error to user
        const errorText = await response.text()
        alert(`Failed to approve high confidence answers: ${errorText || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error bulk approving:', error)
      // Show error to user instead of silently failing
      alert(`Error bulk approving answers: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const approveBulkSelected = async () => {
    if (!currentTenant || !scanResults || selectedAnswers.size === 0) return

    const selectedItems = scanResults.answers.filter(a => selectedAnswers.has(a.questionId))
    if (selectedItems.length === 0) return

    try {
      const response = await fetch('/api/save-kb-answers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: currentTenant.id,
          answers: selectedItems,
          replaceAll: false
        })
      })

      if (response.ok) {
        // Remove approved answers
        setScanResults({
          ...scanResults,
          answers: scanResults.answers.filter(a => !selectedAnswers.has(a.questionId))
        })
        setSelectedAnswers(new Set())
        loadSavedKnowledgeBase()
      }
    } catch (error) {
      console.error('Error bulk approving:', error)
    }
  }

  const generateResponses = async (responseType: 'positive' | 'negative') => {
    console.log('generateResponses called', { responseType, selectedCount: selectedQuestionsForGen.size, hasApiKey, currentTenant: !!currentTenant })

    if (!currentTenant || selectedQuestionsForGen.size === 0 || !hasApiKey) {
      alert('Please select questions and ensure you have an Anthropic API key configured')
      return
    }

    setGenerating(true)
    try {
      const questions = Array.from(selectedQuestionsForGen).map(qId => {
        for (const domain of CAIQ_V4) {
          const question = domain.questions.find(q => q.id === qId)
          if (question) {
            return {
              questionId: qId,
              question: question.question,
              domain: domain.name,
              domainId: domain.id
            }
          }
        }
        return null
      }).filter(Boolean)

      console.log('Prepared questions for generation:', questions)

      const requestBody = {
        tenantId: currentTenant.id,
        questions,
        responseType,
        companyName: currentTenant.name
      }

      console.log('Sending request to /api/generate-caiq-responses:', requestBody)

      const response = await fetch('/api/generate-caiq-responses', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })

      console.log('Response status:', response.status)

      if (response.ok) {
        const data = await response.json()
        console.log('Generated responses:', data)
        setGeneratedResponses(data.responses || [])
        setShowGeneratedReview(true)
        setSelectedQuestionsForGen(new Set())
        alert(`Successfully generated ${data.responses?.length || 0} responses!`)
      } else {
        const error = await response.json()
        console.error('Generation failed:', error)

        // Show detailed error message for API key issues
        if (response.status === 401 && error.troubleshooting) {
          const troubleshootingSteps = error.troubleshooting.join('\n• ')
          alert(`${error.error}\n\n${error.details}\n\nSteps to fix:\n• ${troubleshootingSteps}`)
        } else {
          alert(`Failed to generate responses: ${error.error || error.details || 'Unknown error'}`)
        }
      }
    } catch (error) {
      console.error('Error generating responses:', error)
      alert(`Failed to generate responses: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setGenerating(false)
    }
  }

  const approveGeneratedResponses = async (indices: number[]) => {
    if (!currentTenant || indices.length === 0) return

    const responsesToApprove = indices.map(i => generatedResponses[i]).filter(Boolean)
    if (responsesToApprove.length === 0) return

    try {
      const authToken = localStorage.getItem('auth-token')
      const response = await fetch('/api/save-kb-answers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || ''}`
        },
        credentials: 'include',
        body: JSON.stringify({
          tenantId: currentTenant.id,
          answers: responsesToApprove,
          replaceAll: false
        })
      })

      if (response.ok) {
        // Remove approved responses from the list
        setGeneratedResponses(prev => prev.filter((_, i) => !indices.includes(i)))
        setSelectedGeneratedForApproval(new Set())

        // Delay to account for blob storage eventual consistency
        setTimeout(() => {
          loadSavedKnowledgeBase(true) // Force reload with cache busting
        }, 1000)

        if (generatedResponses.length === indices.length) {
          // All responses were approved, close the review modal
          setShowGeneratedReview(false)
        }

        alert(`Successfully approved ${responsesToApprove.length} answer${responsesToApprove.length !== 1 ? 's' : ''}!`)
      }
    } catch (error) {
      console.error('Error saving generated responses:', error)
      alert('Failed to save responses')
    }
  }

  const rejectGeneratedResponses = (indices: number[]) => {
    setGeneratedResponses(prev => prev.filter((_, i) => !indices.includes(i)))
    setSelectedGeneratedForApproval(new Set())

    if (generatedResponses.length === indices.length) {
      // All responses were rejected, close the review modal
      setShowGeneratedReview(false)
    }
  }

  // Query Tab Handlers
  const handleQuery = async () => {
    if (!currentTenant || !queryText.trim() || !hasApiKey) return

    setQuerying(true)
    setQueryResults([])

    try {
      // Get auth token from localStorage (not cookies)
      const authToken = localStorage.getItem('auth-token')

      const response = await fetch('/api/query-knowledge-base', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || ''}`
        },
        credentials: 'include',
        body: JSON.stringify({
          tenantId: currentTenant.id,
          query: queryText
        })
      })

      const data = await response.json()
      if (response.ok) {
        setQueryResults(data.results || [])
      } else {
        alert(data.error || 'Failed to query knowledge base')
      }
    } catch (error) {
      console.error('Query error:', error)
      alert('Failed to query knowledge base')
    } finally {
      setQuerying(false)
    }
  }

  const handleCopyResult = async (result: any, idx: number) => {
    try {
      // Format the result content
      let textToCopy = `Question: ${result.question}\n\n`
      textToCopy += `Answer: ${result.answer}\n\n`

      if (result.sources && result.sources.length > 0) {
        textToCopy += `Sources: ${result.sources.join(', ')}\n\n`
      }

      textToCopy += `Confidence: ${Math.round(result.confidence * 100)}%`

      // Copy to clipboard
      await navigator.clipboard.writeText(textToCopy)

      // Show visual feedback
      setCopiedIndex(idx)
      setTimeout(() => setCopiedIndex(null), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
      alert('Failed to copy to clipboard')
    }
  }

  // Questionnaire Tab Handlers
  const handleQuestionnaireUpload = async (file: File | null) => {
    if (!file) return

    setQuestionnaireFile(file)

    try {
      // Parse the file
      const text = await file.text()

      // Proper CSV parsing that handles quoted multi-line fields
      const parseCSV = (csvText: string) => {
        const rows: string[][] = []
        let currentRow: string[] = []
        let currentField = ''
        let inQuotes = false

        for (let i = 0; i < csvText.length; i++) {
          const char = csvText[i]
          const nextChar = csvText[i + 1]

          if (char === '"') {
            if (inQuotes && nextChar === '"') {
              // Escaped quote
              currentField += '"'
              i++ // Skip next quote
            } else {
              // Toggle quote mode
              inQuotes = !inQuotes
            }
          } else if (char === ',' && !inQuotes) {
            // End of field
            currentRow.push(currentField.trim())
            currentField = ''
          } else if ((char === '\n' || char === '\r') && !inQuotes) {
            // End of row
            if (currentField || currentRow.length > 0) {
              currentRow.push(currentField.trim())
              if (currentRow.some(f => f)) { // Only add non-empty rows
                rows.push(currentRow)
              }
              currentRow = []
              currentField = ''
            }
            // Skip \r\n combinations
            if (char === '\r' && nextChar === '\n') {
              i++
            }
          } else {
            currentField += char
          }
        }

        // Add last field and row
        if (currentField || currentRow.length > 0) {
          currentRow.push(currentField.trim())
          if (currentRow.some(f => f)) {
            rows.push(currentRow)
          }
        }

        return rows
      }

      const rows = parseCSV(text)

      if (rows.length === 0) {
        alert('File is empty')
        return
      }

      // Find question column
      const headers = rows[0].map(h => h.toLowerCase().trim())
      const questionIndex = headers.findIndex(h => h === 'question')

      if (questionIndex === -1) {
        alert('CSV must have a "question" or "Question" column')
        return
      }

      // Extract questions from rows
      const questions = rows.slice(1)
        .filter(row => row[questionIndex]?.trim())
        .map(row => ({
          question: row[questionIndex].trim().replace(/\s+/g, ' ') // Normalize whitespace
        }))

      console.log('Parsed questions:', questions.length)
      setQuestionnaireQuestions(questions)
    } catch (error) {
      console.error('Error parsing CSV:', error)
      alert('Failed to parse CSV file. Please ensure it is a valid CSV with a "question" column.')
    }
  }

  const handleProcessQuestionnaire = async () => {
    if (!currentTenant || !hasApiKey) return

    setProcessingQuestionnaire(true)

    try {
      const authToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('auth-token='))
        ?.split('=')[1]

      const response = await fetch('/api/process-questionnaire', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || ''}`
        },
        credentials: 'include',
        body: JSON.stringify({
          tenantId: currentTenant.id,
          questions: questionnaireQuestions
        })
      })

      const data = await response.json()
      if (response.ok) {
        // Separate KB answers from unknowns
        const kbAnswers = (data.answers || []).filter((a: any) => a.fromKB)
        const unknowns = (data.unknowns || [])

        setQuestionnaireAnswers(kbAnswers)
        setQuestionnaireUnknowns(unknowns)

        // If there are unknowns, prompt for response type
        if (unknowns.length > 0) {
          setShowGeneratePrompt(true)
        }
      } else {
        alert(data.error || 'Failed to process questionnaire')
      }
    } catch (error) {
      console.error('Processing error:', error)
      alert('Failed to process questionnaire')
    } finally {
      setProcessingQuestionnaire(false)
    }
  }

  const handleGenerateForUnknowns = async () => {
    if (!currentTenant || !hasApiKey) return

    setShowGeneratePrompt(false)
    setProcessingQuestionnaire(true)

    try {
      const authToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('auth-token='))
        ?.split('=')[1]

      const response = await fetch('/api/generate-questionnaire-responses', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || ''}`
        },
        credentials: 'include',
        body: JSON.stringify({
          tenantId: currentTenant.id,
          questions: questionnaireUnknowns,
          responseType: selectedResponseType,
          companyName: currentTenant.name
        })
      })

      const data = await response.json()
      if (response.ok) {
        // Merge generated answers with KB answers
        setQuestionnaireAnswers(prev => [...prev, ...(data.answers || [])])
        setQuestionnaireUnknowns([])
      } else {
        alert(data.error || 'Failed to generate responses')
      }
    } catch (error) {
      console.error('Generation error:', error)
      alert('Failed to generate responses')
    } finally {
      setProcessingQuestionnaire(false)
    }
  }

  const handleGenerateSelectedQuestions = async (responseType: 'positive' | 'negative') => {
    if (!currentTenant || !hasApiKey || selectedQuestions.size === 0) return

    setGeneratingQuestions(true)

    try {
      const authToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('auth-token='))
        ?.split('=')[1]

      const questionsToGenerate = Array.from(selectedQuestions).map(idx => questionnaireQuestions[idx])

      const response = await fetch('/api/generate-questionnaire-responses', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || ''}`
        },
        credentials: 'include',
        body: JSON.stringify({
          tenantId: currentTenant.id,
          questions: questionsToGenerate,
          responseType,
          companyName: currentTenant.name
        })
      })

      const data = await response.json()
      if (response.ok) {
        setQuestionnaireAnswers(prev => [...prev, ...(data.answers || [])])
        setSelectedQuestions(new Set())
      } else {
        alert(data.error || 'Failed to generate responses')
      }
    } catch (error) {
      console.error('Generation error:', error)
      alert('Failed to generate responses')
    } finally {
      setGeneratingQuestions(false)
    }
  }

  const handleSaveQuestionnaireAnswers = async () => {
    if (!currentTenant) return

    try {
      const authToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('auth-token='))
        ?.split('=')[1]

      // Check for duplicates in existing KB
      const answersToSave = questionnaireAnswers.map(a => {
        // Create a normalized question for matching
        const normalizedQuestion = a.question.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim()

        // Check if similar question exists in savedAnswers
        const existingAnswer = Object.values(savedAnswers).find((saved: any) => {
          const savedQuestion = (saved.questionId || '').toLowerCase().replace(/[^a-z0-9\s]/g, '')
          const savedExplanation = (saved.explanation || '').toLowerCase().replace(/[^a-z0-9\s]/g, '')

          // Check if the questions are very similar (keyword overlap)
          const questionWords = normalizedQuestion.split(' ').filter(w => w.length > 3)
          const savedWords = savedQuestion.split(' ').filter(w => w.length > 3)
          const overlap = questionWords.filter(w => savedWords.includes(w)).length
          const similarity = overlap / Math.max(questionWords.length, 1)

          return similarity > 0.7 // 70% keyword overlap
        })

        if (existingAnswer) {
          // Update existing answer
          return {
            ...existingAnswer,
            explanation: a.answer,
            answer: a.fromKB ? existingAnswer.answer : (a.answer.toLowerCase().includes('yes') ? 'yes' : a.answer.toLowerCase().includes('no') ? 'no' : 'unclear'),
            confidence: a.confidence || existingAnswer.confidence || 0.9,
            sources: a.sources || existingAnswer.sources || [],
            status: a.fromKB ? 'approved' : 'ai_draft',
            updatedAt: new Date().toISOString()
          }
        } else {
          // Create new answer
          return {
            questionId: `custom-${normalizedQuestion.substring(0, 50).replace(/\s/g, '-')}`,
            domain: 'custom',
            answer: a.answer.toLowerCase().includes('yes') ? 'yes' : a.answer.toLowerCase().includes('no') ? 'no' : 'unclear',
            explanation: a.answer,
            confidence: a.confidence || 0.9,
            sources: a.sources || [],
            status: a.fromKB ? 'approved' : 'ai_draft',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        }
      })

      const response = await fetch('/api/save-kb-answers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || ''}`
        },
        credentials: 'include',
        body: JSON.stringify({
          tenantId: currentTenant.id,
          answers: answersToSave,
          replaceAll: false
        })
      })

      if (response.ok) {
        // Preserve questionnaire files
        await preserveQuestionnaireFiles()

        alert('Answers saved to knowledge base')
        setQuestionnaireAnswers([])
        setQuestionnaireQuestions([])
        setQuestionnaireFile(null)
        setSelectedQuestions(new Set())
        loadSavedKnowledgeBase()
      } else {
        alert('Failed to save answers')
      }
    } catch (error) {
      console.error('Save error:', error)
      alert('Failed to save answers')
    }
  }

  const handleExportQuestionnaire = () => {
    const csv = [
      'Question,Answer,Source,Confidence',
      ...questionnaireAnswers.map(a =>
        `"${a.question}","${a.answer}","${a.fromKB ? 'Knowledge Base' : 'AI Generated'}","${Math.round((a.confidence || 0) * 100)}%"`
      )
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `questionnaire-responses-${Date.now()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Load preserved questionnaires from backend
  const loadPreservedQuestionnaires = async () => {
    if (!currentTenant) return

    setLoadingPreservedQuestionnaires(true)
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/questionnaire-files-list', {
        method: 'GET',
        credentials: 'include',
        headers
      })

      if (response.ok) {
        const data = await response.json()
        setPreservedQuestionnaires(data.questionnaires || [])
      }
    } catch (error) {
      console.error('Error loading preserved questionnaires:', error)
    } finally {
      setLoadingPreservedQuestionnaires(false)
    }
  }

  // Preserve questionnaire files after answering
  const preserveQuestionnaireFiles = async () => {
    if (!currentTenant || !questionnaireFile) return

    try {
      const authToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('auth-token='))
        ?.split('=')[1]

      // Create answered CSV file
      const csv = [
        'Question,Answer,Source,Confidence',
        ...questionnaireAnswers.map(a =>
          `"${a.question}","${a.answer}","${a.fromKB ? 'Knowledge Base' : 'AI Generated'}","${Math.round((a.confidence || 0) * 100)}%"`
        )
      ].join('\n')
      const answeredBlob = new Blob([csv], { type: 'text/csv' })
      const answeredFile = new File([answeredBlob], `answered-${questionnaireFile.name}`, { type: 'text/csv' })

      // Upload both files
      const formData = new FormData()
      formData.append('originalFile', questionnaireFile)
      formData.append('answeredFile', answeredFile)

      const response = await fetch('/api/questionnaire-files-upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken || ''}`
        },
        credentials: 'include',
        body: formData
      })

      if (response.ok) {
        console.log('Questionnaire files preserved successfully')
        // Reload preserved questionnaires list
        loadPreservedQuestionnaires()
      } else {
        console.error('Failed to preserve questionnaire files')
      }
    } catch (error) {
      console.error('Error preserving questionnaire files:', error)
    }
  }

  // Download a preserved questionnaire file
  const downloadPreservedFile = async (fileId: string, fileName: string) => {
    try {
      const authToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('auth-token='))
        ?.split('=')[1]

      const response = await fetch(`/api/questionnaire-files-download?fileId=${encodeURIComponent(fileId)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken || ''}`
        },
        credentials: 'include'
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        a.click()
        URL.revokeObjectURL(url)
      } else {
        alert('Failed to download file')
      }
    } catch (error) {
      console.error('Error downloading file:', error)
      alert('Failed to download file')
    }
  }

  const saveEditedAnswer = async () => {
    if (!editingAnswer || !currentTenant) return

    try {
      const response = await fetch('/api/save-kb-answers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: currentTenant.id,
          answers: [editingAnswer],
          replaceAll: false
        })
      })

      if (response.ok) {
        // Release lock
        await releaseEditLock(editingAnswer.questionId)

        // Remove from pending
        if (scanResults) {
          setScanResults({
            ...scanResults,
            answers: scanResults.answers.filter(a => a.questionId !== editingAnswer.questionId)
          })
        }
        setEditingAnswer(null)
        loadSavedKnowledgeBase()
      } else {
        // Consume response body and show error to user
        const errorText = await response.text()
        alert(`Failed to save edited answer: ${errorText || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error saving edited answer:', error)
      // Show error to user instead of silently failing
      alert(`Error saving edited answer: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const rejectAnswer = (answer: any) => {
    if (!scanResults) return

    // Just remove from pending without saving
    setScanResults({
      ...scanResults,
      answers: scanResults.answers.filter(a => a.questionId !== answer.questionId)
    })
  }

  const openManualEntry = (question: any, domain: string) => {
    setManualEntryQuestion({ ...question, domain })
    setManualAnswer({
      answer: 'yes',
      explanation: '',
      confidence: 1.0,
      sources: []
    })
  }

  const saveManualAnswer = async () => {
    if (!manualEntryQuestion || !currentTenant) return

    try {
      const answerToSave = {
        questionId: manualEntryQuestion.id,
        domain: manualEntryQuestion.domain,
        answer: manualAnswer.answer,
        explanation: manualAnswer.explanation,
        confidence: manualAnswer.confidence,
        sources: manualAnswer.sources
      }

      const response = await fetch('/api/save-kb-answers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: currentTenant.id,
          answers: [answerToSave],
          replaceAll: false
        })
      })

      if (response.ok) {
        setManualEntryQuestion(null)
        loadSavedKnowledgeBase()
      }
    } catch (error) {
      console.error('Error saving manual answer:', error)
    }
  }

  const getFilteredAnswers = () => {
    if (!scanResults) return []

    let filtered = scanResults.answers

    // Filter by confidence
    if (confidenceFilter === 'high') {
      filtered = filtered.filter((a: any) => a.confidence > 0.8)
    } else if (confidenceFilter === 'medium') {
      filtered = filtered.filter((a: any) => a.confidence > 0.5 && a.confidence <= 0.8)
    } else if (confidenceFilter === 'low') {
      filtered = filtered.filter((a: any) => a.confidence <= 0.5)
    }

    // Filter by conflicts
    if (showConflictsOnly) {
      filtered = filtered.filter((a: any) => a.conflicts === true)
    }

    return filtered
  }

  const getConfidenceColor = (confidence: number) => {
    if (confidence > 0.8) return 'text-green-600 bg-green-50 border-green-200'
    if (confidence > 0.5) return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    return 'text-red-600 bg-red-50 border-red-200'
  }

  const getConfidenceLabel = (confidence: number) => {
    if (confidence > 0.8) return 'High Confidence'
    if (confidence > 0.5) return 'Medium Confidence'
    return 'Low Confidence'
  }

  const getStatusBadge = (status?: string) => {
    const statusValue = status || 'draft'
    const colors = {
      draft: 'bg-gray-100 text-gray-700 border-gray-300',
      ai_draft: 'bg-blue-100 text-blue-700 border-blue-300',
      under_review: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      approved: 'bg-green-100 text-green-700 border-green-300',
      needs_update: 'bg-red-100 text-red-700 border-red-300'
    }
    const labels = {
      draft: 'Draft',
      ai_draft: 'AI Draft',
      under_review: 'Under Review',
      approved: 'Approved',
      needs_update: 'Needs Update'
    }
    return { color: colors[statusValue as keyof typeof colors], label: labels[statusValue as keyof typeof labels] }
  }

  const updateAnswerStatus = async (questionId: string, newStatus: string, reviewNotes?: string) => {
    if (!currentTenant) return

    const answer = savedAnswers[questionId]
    if (!answer) return

    try {
      const response = await fetch('/api/save-kb-answers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: currentTenant.id,
          answers: [{
            ...answer,
            status: newStatus,
            lastReviewedAt: new Date().toISOString(),
            lastReviewedBy: adminEmail,
            reviewNotes: reviewNotes || answer.reviewNotes
          }],
          replaceAll: false
        })
      })

      if (response.ok) {
        loadSavedKnowledgeBase()
      }
    } catch (error) {
      console.error('Error updating status:', error)
    }
  }

  const getFilteredDomains = () => {
    let filteredDomains = CAIQ_V4

    // Filter by selected domain
    if (selectedDomain !== 'all') {
      filteredDomains = filteredDomains.filter(d => d.id === selectedDomain)
    }

    // Apply search and status filter to questions
    return filteredDomains.map(domain => {
      let filteredQuestions = domain.questions

      // Filter by search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase()
        filteredQuestions = filteredQuestions.filter(q =>
          q.id.toLowerCase().includes(query) ||
          q.question.toLowerCase().includes(query) ||
          domain.name.toLowerCase().includes(query)
        )
      }

      // Filter by status
      if (statusFilter === 'answered') {
        filteredQuestions = filteredQuestions.filter(q => savedAnswers[q.id])
      } else if (statusFilter === 'unanswered') {
        filteredQuestions = filteredQuestions.filter(q => !savedAnswers[q.id])
      }

      // Filter by review status
      if (reviewStatusFilter !== 'all') {
        filteredQuestions = filteredQuestions.filter(q => {
          const answer = savedAnswers[q.id]
          if (!answer) return false
          return (answer.status || 'draft') === reviewStatusFilter
        })
      }

      return {
        ...domain,
        questions: filteredQuestions
      }
    }).filter(domain => domain.questions.length > 0)
  }

  const exportToCSV = () => {
    const filteredDomains = getFilteredDomains()
    const rows: string[][] = [
      ['Question ID', 'Domain', 'Question', 'Answer', 'Explanation', 'Confidence', 'Last Updated']
    ]

    filteredDomains.forEach(domain => {
      domain.questions.forEach(q => {
        const answer = savedAnswers[q.id]
        if (answer) {
          rows.push([
            q.id,
            domain.name,
            q.question,
            answer.answer || '',
            answer.explanation || '',
            answer.confidence?.toString() || '',
            answer.lastUpdated ? new Date(answer.lastUpdated).toLocaleString() : ''
          ])
        }
      })
    })

    const csvContent = rows.map(row =>
      row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')
    ).join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `knowledge-base-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  const exportToJSON = () => {
    const filteredDomains = getFilteredDomains()
    const exportData = {
      tenantId: currentTenant?.id,
      exportedAt: new Date().toISOString(),
      completion: completion,
      domains: filteredDomains.map(domain => ({
        id: domain.id,
        name: domain.name,
        description: domain.description,
        questions: domain.questions.map(q => {
          const answer = savedAnswers[q.id]
          return {
            id: q.id,
            question: q.question,
            answer: answer || null
          }
        }).filter(q => q.answer !== null)
      })).filter(d => d.questions.length > 0)
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `knowledge-base-${new Date().toISOString().split('T')[0]}.json`
    link.click()
  }

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      const nextChar = line[i + 1]

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"'
          i++
        } else {
          // Toggle quote state
          inQuotes = !inQuotes
        }
      } else if (char === ',' && !inQuotes) {
        // Field separator
        result.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }

    result.push(current.trim())
    return result
  }

  const handleImportFile = async (file: File) => {
    setImportFile(file)
    setImportPreview([])

    try {
      const text = await file.text()

      if (file.name.endsWith('.csv')) {
        // Parse CSV with proper quoted field handling
        const lines = text.split('\n').filter(line => line.trim())
        if (lines.length < 2) {
          alert('CSV file is empty or invalid')
          return
        }

        const preview: any[] = []

        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i])
          if (values.length >= 4) {
            preview.push({
              questionId: values[0],
              domain: values[1],
              question: values[2],
              answer: values[3],
              explanation: values[4] || '',
              confidence: parseFloat(values[5]) || 1.0
            })
          }
        }

        setImportPreview(preview)
      } else if (file.name.endsWith('.json')) {
        // Parse JSON
        const data = JSON.parse(text)
        const preview: any[] = []

        if (data.domains && Array.isArray(data.domains)) {
          data.domains.forEach((domain: any) => {
            if (domain.questions && Array.isArray(domain.questions)) {
              domain.questions.forEach((q: any) => {
                if (q.answer) {
                  preview.push({
                    questionId: q.id,
                    domain: domain.name,
                    question: q.question,
                    ...q.answer
                  })
                }
              })
            }
          })
        }

        setImportPreview(preview)
      } else {
        alert('Please upload a CSV or JSON file')
      }
    } catch (error) {
      console.error('Error parsing import file:', error)
      alert('Error parsing file. Please check the format.')
    }
  }

  const executeImport = async () => {
    if (!currentTenant || importPreview.length === 0) return

    setImporting(true)

    try {
      const response = await fetch('/api/save-kb-answers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: currentTenant.id,
          answers: importPreview,
          replaceAll: importStrategy === 'replace'
        })
      })

      if (response.ok) {
        const result = await response.json()
        alert(`Successfully imported ${result.saved} answers. Completion: ${result.completion}%`)
        setShowImportModal(false)
        setImportFile(null)
        setImportPreview([])
        loadSavedKnowledgeBase()
      } else {
        alert('Failed to import answers')
      }
    } catch (error) {
      console.error('Error importing:', error)
      alert('Error importing answers')
    } finally {
      setImporting(false)
    }
  }

  // Helper function to format timestamps
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`

    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  // Helper function to get action label and icon
  const getActionDetails = (action: string) => {
    const actionMap: Record<string, { label: string, icon: any, color: string }> = {
      scan_started: { label: 'Started scan', icon: Search, color: 'text-blue-600' },
      scan_resumed: { label: 'Resumed scan', icon: RefreshCw, color: 'text-blue-600' },
      scan_completed: { label: 'Completed scan', icon: CheckCircle, color: 'text-green-600' },
      scan_failed: { label: 'Scan failed', icon: AlertCircle, color: 'text-red-600' },
      progress_cleared: { label: 'Cleared progress', icon: X, color: 'text-orange-600' },
      answer_edited: { label: 'Edited answer', icon: FileText, color: 'text-purple-600' },
      answer_approved: { label: 'Approved answer', icon: CheckCircle, color: 'text-green-600' },
      answer_rejected: { label: 'Rejected answer', icon: X, color: 'text-red-600' },
      bulk_export: { label: 'Exported answers', icon: Download, color: 'text-gray-600' },
      bulk_import: { label: 'Imported answers', icon: Upload, color: 'text-gray-600' }
    }
    return actionMap[action] || { label: action, icon: Clock, color: 'text-gray-600' }
  }

  // Acquire edit lock for an answer
  const acquireEditLock = async (questionId: string) => {
    if (!currentTenant) return false

    try {
      const authToken = localStorage.getItem('auth-token')
      const response = await fetch('/api/kb-edit-lock', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || ''}`
        },
        credentials: 'include',
        body: JSON.stringify({
          tenantId: currentTenant.id,
          questionId
        })
      })

      if (response.status === 409) {
        // Lock conflict - someone else is editing
        const data = await response.json()
        setLockError(data.message)
        return false
      }

      if (response.ok) {
        const data = await response.json()
        setEditLock(data.lock)
        setLockError(null)
        return true
      }

      return false
    } catch (error) {
      console.error('Error acquiring lock:', error)
      setLockError('Failed to acquire edit lock')
      return false
    }
  }

  // Release edit lock
  const releaseEditLock = async (questionId: string) => {
    if (!currentTenant) return

    try {
      const authToken = localStorage.getItem('auth-token')
      await fetch('/api/kb-edit-lock', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || ''}`
        },
        credentials: 'include',
        body: JSON.stringify({
          tenantId: currentTenant.id,
          questionId
        })
      })

      setEditLock(null)
      setLockError(null)
    } catch (error) {
      console.error('Error releasing lock:', error)
    }
  }

  // Open edit modal with lock acquisition
  const openEditModal = async (answer: any) => {
    const lockAcquired = await acquireEditLock(answer.questionId)
    if (lockAcquired) {
      setEditingAnswer({ ...answer })
    }
  }

  // Close edit modal with lock release
  const closeEditModal = async () => {
    if (editingAnswer) {
      await releaseEditLock(editingAnswer.questionId)
    }
    setEditingAnswer(null)
    setLockError(null)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <BookOpen className="h-8 w-8 text-primary-600" />
              <h1 className="ml-3 text-2xl font-bold text-gray-900">Knowledge Base</h1>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowActivityLog(!showActivityLog)}
                className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                <Clock className="h-4 w-4 mr-2" />
                Activity Log
                {activityLogs.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                    {activityLogs.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Status Bar */}
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-900">CAIQ v{CAIQ_VERSION} Completion</p>
                <p className="text-xs text-blue-700 mt-1">{Math.round(completion)}% complete ({Math.round(completion * TOTAL_QUESTIONS / 100)}/{TOTAL_QUESTIONS} questions)</p>
              </div>
              <div className="w-64 bg-blue-200 rounded-full h-3">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all"
                  style={{ width: `${completion}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            {/* Mobile: Hamburger + Current Tab */}
            <div className="md:hidden flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                {(() => {
                  const allTabs = [
                    { key: 'query', label: 'Query', icon: Search },
                    { key: 'questionnaire', label: 'Questionnaire', icon: FileText, requiresFlag: 'questionnaire' },
                    { key: 'caiq', label: 'CAIQ', icon: BookOpen },
                    { key: 'tprm', label: 'TPRM', icon: Database, requiresFlag: 'tprm' },
                    { key: 'vendor-risk', label: 'Vendor Risk', icon: Shield, requiresFlag: 'vendorRisk' }
                  ]
                  const visibleTabs = allTabs.filter(t => !t.requiresFlag || featureFlags[t.requiresFlag as keyof typeof featureFlags])
                  const currentTab = visibleTabs.find(t => t.key === activeTab)
                  const Icon = currentTab?.icon || Search
                  return (
                    <>
                      <Icon className="h-5 w-5 text-primary-600" />
                      <span className="font-medium text-gray-900">{currentTab?.label}</span>
                    </>
                  )
                })()}
              </div>
              <button
                onClick={() => setSubMenuOpen(!subMenuOpen)}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                aria-label="Toggle sub menu"
              >
                {subMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>

            {/* Mobile: Dropdown Menu */}
            {subMenuOpen && (
              <div className="md:hidden border-t border-gray-200 py-2">
                {[
                  { key: 'query', label: 'Query', icon: Search },
                  { key: 'questionnaire', label: 'Questionnaire', icon: FileText, requiresFlag: 'questionnaire' },
                  { key: 'caiq', label: 'CAIQ', icon: BookOpen },
                  { key: 'tprm', label: 'TPRM', icon: Database, requiresFlag: 'tprm' },
                  { key: 'vendor-risk', label: 'Vendor Risk', icon: Shield, requiresFlag: 'vendorRisk' }
                ].filter(t => !t.requiresFlag || featureFlags[t.requiresFlag as keyof typeof featureFlags]).map((tab) => {
                  const IconComponent = tab.icon
                  return (
                    <button
                      key={tab.key}
                      onClick={() => {
                        setActiveTab(tab.key as any)
                        setSubMenuOpen(false)
                      }}
                      className={`w-full flex items-center px-4 py-3 text-sm font-medium ${
                        activeTab === tab.key
                          ? 'bg-primary-50 text-primary-600'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <IconComponent className="h-4 w-4 mr-3" />
                      {tab.label}
                    </button>
                  )
                })}
              </div>
            )}

            {/* Desktop: Horizontal Tabs */}
            <nav className="hidden md:flex space-x-8 px-6">
              {[
                { key: 'query', label: 'Query', icon: Search },
                { key: 'questionnaire', label: 'Questionnaire', icon: FileText, requiresFlag: 'questionnaire' },
                { key: 'caiq', label: 'CAIQ', icon: BookOpen },
                { key: 'tprm', label: 'TPRM', icon: Database, requiresFlag: 'tprm' },
                { key: 'vendor-risk', label: 'Vendor Risk', icon: Shield, requiresFlag: 'vendorRisk' }
              ].filter(t => !t.requiresFlag || featureFlags[t.requiresFlag as keyof typeof featureFlags]).map((tab) => {
                const IconComponent = tab.icon
                return (
                  <button
                    key={tab.key}
                    data-tour-subtab={tab.key}
                    onClick={() => setActiveTab(tab.key as any)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.key
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <IconComponent className="h-4 w-4 mr-2" />
                    {tab.label}
                  </button>
                )
              })}
            </nav>
          </div>

          <div className="p-6">
            {/* Query Tab */}
            {activeTab === 'query' && (
              <div className="space-y-6">
                {/* Tab Explanation */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Search className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">AI-Powered Query Interface</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        Ask natural language questions and get AI-generated answers based on your uploaded documents and knowledge base.
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 mt-1">•</span>
                          <span>Search across all uploaded documents, policies, and compliance materials</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 mt-1">•</span>
                          <span>Get instant answers with source citations and confidence scores</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 mt-1">•</span>
                          <span>Save responses for future reference and sharing with stakeholders</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* API Key Warning */}
                {!hasApiKey && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5" />
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Anthropic API Key Required
                        </h3>
                        <p className="text-sm text-yellow-700 mt-1">
                          AI-powered querying requires an Anthropic API key. You are responsible for API usage costs.
                        </p>
                        <div className="mt-3 flex items-center gap-3">
                          <button
                            onClick={() => onNavigateToSettings?.()}
                            className="text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                          >
                            Add API key in Settings →
                          </button>
                          <a
                            href="https://console.anthropic.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                          >
                            Get API key from Anthropic
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {hasApiKey && apiKeySource === 'tenant' && (
                  <div className="bg-green-50 border-l-4 border-green-400 p-4">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">
                          Ready to Query
                        </h3>
                        <p className="text-sm text-green-700 mt-1">
                          Ask questions about your uploaded documents and knowledge base using your own API key.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {hasApiKey && apiKeySource === 'platform' && (
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-blue-400 mt-0.5" />
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-blue-800">
                          Using Platform-Provided API Key
                        </h3>
                        <p className="text-sm text-blue-700 mt-1">
                          You're using a shared API key provided by the platform. You can add your own key for full control.
                        </p>
                        <div className="mt-3">
                          <button
                            onClick={() => onNavigateToSettings?.()}
                            className="text-sm font-medium text-blue-800 hover:text-blue-900 underline"
                          >
                            Add your own API key →
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-lg border border-gray-200 p-6" data-tour="kb-query">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Ask a Question</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <textarea
                        value={queryText}
                        onChange={(e) => setQueryText(e.target.value)}
                        placeholder="e.g., What is our data retention policy? How do we handle GDPR requests?"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                        rows={4}
                      />
                    </div>
                    <button
                      onClick={() => handleQuery()}
                      disabled={!queryText.trim() || !hasApiKey || querying}
                      className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium"
                    >
                      {querying ? (
                        <>
                          <Loader className="h-5 w-5 animate-spin mr-2" />
                          Searching...
                        </>
                      ) : (
                        <>
                          <Search className="h-5 w-5 mr-2" />
                          Search Knowledge Base
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Results */}
                {queryResults.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Results</h3>
                    <div className="space-y-4">
                      {queryResults.map((result, idx) => (
                        <div key={idx} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-gray-900 flex-1">{result.question}</h4>
                            <div className="flex items-center gap-2 ml-2">
                              <button
                                onClick={() => handleCopyResult(result, idx)}
                                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                                title="Copy result"
                              >
                                {copiedIndex === idx ? (
                                  <Check className="h-4 w-4 text-green-600" />
                                ) : (
                                  <Copy className="h-4 w-4 text-gray-500" />
                                )}
                              </button>
                              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full whitespace-nowrap">
                                {Math.round(result.confidence * 100)}% match
                              </span>
                            </div>
                          </div>
                          <div className="prose prose-sm max-w-none text-gray-700 mb-3">
                            <ReactMarkdown>{result.answer}</ReactMarkdown>
                          </div>
                          {result.sources && result.sources.length > 0 && (
                            <div className="text-xs text-gray-500">
                              <span className="font-medium">Sources:</span> {result.sources.join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Questionnaire Tab */}
            {activeTab === 'questionnaire' && featureFlags.questionnaire && (
              <div className="space-y-6">
                {/* Tab Explanation */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <FileText className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Bulk Questionnaire Processing</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        Upload customer security questionnaires and automatically generate AI-powered responses based on your knowledge base.
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li className="flex items-start gap-2">
                          <span className="text-green-600 mt-1">•</span>
                          <span>Upload CSV or Excel files with questions to process in bulk</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-green-600 mt-1">•</span>
                          <span>AI analyzes your documents to generate accurate, consistent responses</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-green-600 mt-1">•</span>
                          <span>Review and edit responses before exporting to Excel or CSV</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-green-600 mt-1">•</span>
                          <span>Track confidence levels and identify questions needing human review</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* API Key Warning */}
                {!hasApiKey && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5" />
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Anthropic API Key Required
                        </h3>
                        <p className="text-sm text-yellow-700 mt-1">
                          AI-powered questionnaire responses require an Anthropic API key. You are responsible for API usage costs.
                        </p>
                        <div className="mt-3 flex items-center gap-3">
                          <button
                            onClick={() => onNavigateToSettings?.()}
                            className="text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                          >
                            Add API key in Settings →
                          </button>
                          <a
                            href="https://console.anthropic.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                          >
                            Get API key from Anthropic
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {hasApiKey && (
                  <div className="bg-green-50 border-l-4 border-green-400 p-4">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">
                          Ready to Process Questionnaires
                        </h3>
                        <p className="text-sm text-green-700 mt-1">
                          Upload a CSV or Excel file with questions to generate AI-powered responses based on your knowledge base.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Upload Section */}
                {!questionnaireQuestions.length && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6" data-tour="kb-questionnaire-upload">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Questionnaire</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Upload a CSV or Excel file with your questions. The file should have a column named "question" or "Question".
                    </p>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={(e) => handleQuestionnaireUpload(e.target.files?.[0] || null)}
                        className="hidden"
                        id="questionnaire-upload"
                      />
                      <label
                        htmlFor="questionnaire-upload"
                        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 cursor-pointer"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Choose File
                      </label>
                      {questionnaireFile && (
                        <p className="text-sm text-gray-600 mt-2">
                          Selected: {questionnaireFile.name}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Questions Preview & Processing */}
                {questionnaireQuestions.length > 0 && questionnaireAnswers.length === 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {questionnaireQuestions.length} Questions Loaded
                        {selectedQuestions.size > 0 && ` (${selectedQuestions.size} selected)`}
                      </h3>
                      <button
                        onClick={() => {
                          setQuestionnaireQuestions([])
                          setQuestionnaireFile(null)
                          setQuestionnaireAnswers([])
                          setSelectedQuestions(new Set())
                        }}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Clear & Upload New
                      </button>
                    </div>

                    {/* Generation Buttons */}
                    <div className="mb-4 flex gap-3">
                      <div className="flex-1 flex gap-2">
                        <button
                          onClick={() => handleGenerateSelectedQuestions('positive')}
                          disabled={!hasApiKey || selectedQuestions.size === 0 || generatingQuestions}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium text-sm"
                        >
                          {generatingQuestions ? (
                            <Loader className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2" />
                          )}
                          Generate Positive ({selectedQuestions.size})
                        </button>
                        <button
                          onClick={() => handleGenerateSelectedQuestions('negative')}
                          disabled={!hasApiKey || selectedQuestions.size === 0 || generatingQuestions}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium text-sm"
                        >
                          {generatingQuestions ? (
                            <Loader className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <X className="h-4 w-4 mr-2" />
                          )}
                          Generate Negative ({selectedQuestions.size})
                        </button>
                      </div>
                      <button
                        onClick={() => handleProcessQuestionnaire()}
                        disabled={!hasApiKey || processingQuestionnaire}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium text-sm"
                      >
                        {processingQuestionnaire ? (
                          <Loader className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Search className="h-4 w-4 mr-2" />
                        )}
                        Search KB
                      </button>
                    </div>

                    {/* Select All */}
                    <div className="mb-3 pb-3 border-b border-gray-200">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedQuestions.size === questionnaireQuestions.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedQuestions(new Set(questionnaireQuestions.map((_, idx) => idx)))
                            } else {
                              setSelectedQuestions(new Set())
                            }
                          }}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">
                          Select All Questions
                        </span>
                      </label>
                    </div>

                    {/* Questions List with Checkboxes */}
                    <div className="max-h-96 overflow-y-auto space-y-2">
                      {questionnaireQuestions.map((q, idx) => (
                        <div key={idx} className="border border-gray-200 rounded p-3">
                          <label className="flex items-start cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedQuestions.has(idx)}
                              onChange={(e) => {
                                const newSelected = new Set(selectedQuestions)
                                if (e.target.checked) {
                                  newSelected.add(idx)
                                } else {
                                  newSelected.delete(idx)
                                }
                                setSelectedQuestions(newSelected)
                              }}
                              className="h-4 w-4 mt-0.5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <div className="ml-3 flex-1">
                              <span className="font-medium text-gray-700 text-sm">Q{idx + 1}:</span>
                              <span className="text-sm text-gray-900 ml-1">{q.question}</span>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Answers for Review */}
                {questionnaireAnswers.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Generated Responses</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Review and edit responses before saving to your knowledge base.
                    </p>
                    <div className="space-y-4">
                      {questionnaireAnswers.map((answer, idx) => (
                        <div key={idx} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-gray-900 text-sm">{answer.question}</h4>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              answer.fromKB
                                ? 'bg-green-100 text-green-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {answer.fromKB ? 'From KB' : 'AI Generated'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mb-2">{answer.answer}</p>
                          {answer.confidence && (
                            <div className="text-xs text-gray-500">
                              Confidence: {Math.round(answer.confidence * 100)}%
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 flex gap-3">
                      <button
                        onClick={() => handleSaveQuestionnaireAnswers()}
                        className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center font-medium"
                      >
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Save All to Knowledge Base
                      </button>
                      <button
                        onClick={() => handleExportQuestionnaire()}
                        className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center font-medium"
                      >
                        <Download className="h-5 w-5 mr-2" />
                        Export as CSV
                      </button>
                    </div>
                  </div>
                )}

                {/* Preserved Questionnaires - Always visible */}
                {!loadingPreservedQuestionnaires && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Preserved Questionnaires
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      When you save questionnaire answers to the knowledge base, both the original and answered files are preserved here for future reference.
                    </p>

                    {preservedQuestionnaires.length > 0 ? (
                      <div className="space-y-3">
                        {preservedQuestionnaires.map((questionnaire) => (
                          <div key={questionnaire.sessionId} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <File className="h-5 w-5 text-gray-400" />
                                <span className="text-sm font-medium text-gray-700">
                                  {new Date(questionnaire.createdAt).toLocaleDateString()} - {new Date(questionnaire.createdAt).toLocaleTimeString()}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {questionnaire.files.length} file(s)
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              {questionnaire.files.map((file: any) => (
                                <button
                                  key={file.fileId}
                                  onClick={() => downloadPreservedFile(file.fileId, file.fileName)}
                                  className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 text-left"
                                >
                                  <Download className="h-4 w-4 text-gray-600 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                      {file.fileName}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {file.fileType === 'original' ? 'Original' : 'Answered'} • {(file.fileSize / 1024).toFixed(1)} KB
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                        <File className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-900 mb-1">No preserved questionnaires yet</p>
                        <p className="text-sm text-gray-500">
                          Upload and process a questionnaire, then click "Save All to Knowledge Base" to preserve it here.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {loadingPreservedQuestionnaires && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-center">
                      <Loader className="h-5 w-5 animate-spin text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">Loading preserved questionnaires...</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CAIQ Tab */}
            {activeTab === 'caiq' && (
              <div className="space-y-6">
                {/* Tab Explanation */}
                <div className="bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <BookOpen className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">CAIQ (Consensus Assessments Initiative Questionnaire)</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        Automate completion of the industry-standard Cloud Security Alliance CAIQ v4 questionnaire with AI-powered scanning and extraction.
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li className="flex items-start gap-2">
                          <span className="text-purple-600 mt-1">•</span>
                          <span><strong>Configure Sources:</strong> Add your website, documentation, and security pages</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-purple-600 mt-1">•</span>
                          <span><strong>Scan & Extract:</strong> AI automatically finds answers to 285 CAIQ questions</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-purple-600 mt-1">•</span>
                          <span><strong>Review Results:</strong> Validate AI-generated responses and resolve conflicts</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-purple-600 mt-1">•</span>
                          <span><strong>Browse CAIQ:</strong> Explore all questions organized by domain and control</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-purple-600 mt-1">•</span>
                          <span><strong>Analytics:</strong> Track completion progress and identify gaps</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* CAIQ Sub-Tabs */}
                <div className="border-b border-gray-200 -mt-6 -mx-6 px-6">
                  <nav className="flex space-x-8">
                    {[
                      { key: 'configure', label: 'Configure Sources', icon: Globe },
                      { key: 'scan', label: 'Scan & Extract', icon: Search },
                      { key: 'review', label: 'Review Results', icon: CheckCircle, badge: scanResults?.answers.length || 0 },
                      { key: 'browse', label: 'Browse CAIQ', icon: BookOpen },
                      { key: 'analytics', label: 'Analytics', icon: TrendingUp }
                    ].map((tab) => {
                      const IconComponent = tab.icon
                      return (
                        <button
                          key={tab.key}
                          data-tour-subtab={`caiq-${tab.key}`}
                          onClick={() => setCAIQSubTab(tab.key as any)}
                          className={`flex items-center py-3 px-1 border-b-2 font-medium text-sm ${
                            caiqSubTab === tab.key
                              ? 'border-primary-500 text-primary-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <IconComponent className="h-4 w-4 mr-2" />
                          {tab.label}
                          {tab.badge !== undefined && tab.badge > 0 && (
                            <span className="ml-2 bg-primary-600 text-white text-xs rounded-full px-2 py-0.5">
                              {tab.badge}
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </nav>
                </div>

                <div>
            {/* Configure Tab */}
            {caiqSubTab === 'configure' && (
              <div className="space-y-6" data-tour="kb-caiq-configure">
                {/* API Key Status Warning */}
                {!hasApiKey && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5" />
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Anthropic API Key Required
                        </h3>
                        <p className="text-sm text-yellow-700 mt-1">
                          AI-powered CAIQ scanning requires an Anthropic API key. You are responsible for API usage costs (~$0.86-$2.80 per scan).
                        </p>
                        <div className="mt-3 flex items-center gap-3">
                          <button
                            onClick={() => onNavigateToSettings?.()}
                            className="text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                          >
                            Add API key in Settings →
                          </button>
                          <a
                            href="https://console.anthropic.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                          >
                            Get API key from Anthropic
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {hasApiKey && (
                  <div className="bg-green-50 border-l-4 border-green-400 p-4">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">
                          API Key Configured
                        </h3>
                        <p className="text-sm text-green-700 mt-1">
                          Ready to scan documents and generate CAIQ answers with AI.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Uploaded Documents - Primary Source */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-4">
                    <FileText className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">Use Existing Documents</h3>
                      <p className="text-sm text-gray-600">
                        Select from your uploaded documents (SOC 2 reports, security policies, compliance certificates) to extract CAIQ answers.
                        This is typically the most accurate source.
                      </p>
                    </div>
                  </div>

                  {loadingDocs ? (
                    <div className="text-center py-8 bg-white rounded-lg">
                      <Loader className="h-6 w-6 animate-spin mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Loading documents...</p>
                    </div>
                  ) : documents.length === 0 ? (
                    <div className="text-center py-8 bg-white rounded-lg border-2 border-dashed border-gray-300">
                      <Upload className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-700 mb-2">No documents uploaded yet</p>
                      <p className="text-xs text-gray-500 mb-3">
                        Upload security documents in the Content tab first, then return here to select them.
                      </p>
                      <a
                        href="/admin?tab=content"
                        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Go to Content Tab
                      </a>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedDocIds.length === documents.length}
                            onChange={toggleAllDocuments}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">
                            Select All ({selectedDocIds.length}/{documents.length} selected)
                          </span>
                        </label>
                        {selectedDocIds.length > 0 && (
                          <span className="text-xs text-green-600 font-medium">
                            ✓ {selectedDocIds.length} document{selectedDocIds.length !== 1 ? 's' : ''} will be scanned
                          </span>
                        )}
                      </div>

                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {documents.map((doc) => (
                          <label
                            key={doc.id}
                            className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition ${
                              selectedDocIds.includes(doc.id)
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedDocIds.includes(doc.id)}
                              onChange={() => toggleDocument(doc.id)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <FileText className={`h-5 w-5 mx-3 ${
                              selectedDocIds.includes(doc.id) ? 'text-primary-600' : 'text-gray-400'
                            }`} />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{doc.title}</p>
                              <p className="text-xs text-gray-500">
                                {doc.category} • Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                                {doc.extractedText && ` • ${Math.round(doc.extractedText.length / 1000)}k chars extracted`}
                              </p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Web Sources - Secondary Source */}
                <div>
                  <div className="flex items-start gap-3 mb-4">
                    <Globe className="h-6 w-6 text-gray-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">Add Web Sources (Optional)</h3>
                      <p className="text-sm text-gray-600">
                        Add URLs to scan for additional security information. The system will crawl these pages and extract CAIQ answers.
                      </p>
                    </div>
                  </div>

                  {/* Auto-Discovery */}
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">Auto-Discover Security URLs</h4>
                    <p className="text-xs text-blue-700 mb-3">
                      Enter your company domain to automatically find security, privacy, and compliance pages.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={companyDomain}
                        onChange={(e) => setCompanyDomain(e.target.value)}
                        placeholder="company.com"
                        className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <button
                        onClick={discoverUrls}
                        disabled={discovering || !companyDomain}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm"
                      >
                        {discovering ? (
                          <>
                            <Loader className="h-4 w-4 mr-2 animate-spin" />
                            Discovering...
                          </>
                        ) : (
                          <>
                            <Search className="h-4 w-4 mr-2" />
                            Discover
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {sources.map((source, index) => (
                      <div key={index} className={`p-3 border rounded-lg ${
                        source.status === 'error' ? 'border-red-300 bg-red-50' :
                        source.status === 'testing' ? 'border-yellow-300 bg-yellow-50' :
                        'border-gray-200'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start flex-1 min-w-0">
                            <Globe className={`h-5 w-5 mr-3 mt-0.5 flex-shrink-0 ${
                              source.status === 'error' ? 'text-red-500' :
                              source.status === 'testing' ? 'text-yellow-500' :
                              'text-green-500'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900 truncate">{source.url}</p>
                              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                <span>Added {new Date(source.addedAt).toLocaleDateString()}</span>
                                {source.lastScanned && (
                                  <span>Last scanned {new Date(source.lastScanned).toLocaleDateString()}</span>
                                )}
                                {source.answersFound !== undefined && (
                                  <span className="text-green-600 font-medium">{source.answersFound} answers found</span>
                                )}
                              </div>
                              {source.errorMessage && (
                                <p className="text-xs text-red-600 mt-1">{source.errorMessage}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                            <button
                              onClick={() => testUrl(source.url)}
                              disabled={testingUrl === source.url}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                              title="Test URL"
                            >
                              {testingUrl === source.url ? (
                                <Loader className="h-3 w-3 animate-spin" />
                              ) : (
                                'Test'
                              )}
                            </button>
                            <button
                              onClick={() => removeSource(source.url)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={newSource}
                        onChange={(e) => setNewSource(e.target.value)}
                        placeholder="https://company.com/security"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                      <button
                        onClick={addSource}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </button>
                    </div>
                  </div>
                </div>

                {/* Scan Depth */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Scan Depth (Web Sources)</label>
                  <select
                    value={scanDepth}
                    onChange={(e) => setScanDepth(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="5">Fast (up to 5 pages per source)</option>
                    <option value="20">Thorough (up to 20 pages per source)</option>
                    <option value="50">Deep (up to 50 pages per source)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Deeper scans find more answers but take longer
                  </p>
                </div>
              </div>
            )}

            {/* Scan Tab */}
            {caiqSubTab === 'scan' && (
              <div className="space-y-6 text-center py-12" data-tour="kb-caiq-scan">
                <div className="bg-blue-50 rounded-full w-24 h-24 flex items-center justify-center mx-auto">
                  {scanning ? (
                    <Loader className="h-12 w-12 text-blue-600 animate-spin" />
                  ) : (
                    <Search className="h-12 w-12 text-blue-600" />
                  )}
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {scanning ? 'Scanning Sources...' : 'Ready to Scan'}
                  </h3>
                  <p className="text-gray-600">
                    {scanning
                      ? scanProgress
                      : `Will scan ${sources.length} web source${sources.length !== 1 ? 's' : ''} and ${selectedDocIds.length} document${selectedDocIds.length !== 1 ? 's' : ''}`
                    }
                  </p>
                  {!scanning && (sources.length > 0 || selectedDocIds.length > 0) && (
                    <div className="mt-4 max-w-md mx-auto">
                      <div className="flex items-start text-left bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                        <TrendingUp className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-gray-900 mb-1">What will happen:</p>
                          <ul className="space-y-1 list-disc list-inside">
                            {sources.length > 0 && (
                              <li>Crawl {sources.length} web page{sources.length !== 1 ? 's' : ''} for security content</li>
                            )}
                            {selectedDocIds.length > 0 && (
                              <li>Extract text from {selectedDocIds.length} uploaded document{selectedDocIds.length !== 1 ? 's' : ''}</li>
                            )}
                            <li>AI analyzes content against {TOTAL_QUESTIONS} CAIQ questions</li>
                            <li>Generate answers with confidence scores</li>
                            <li>Detect conflicts between sources</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {!scanning && (
                  <div className="space-y-4">
                    {/* Show permission warning if user can't scan */}
                    {!hasPermission('scan') && (
                      <div className="max-w-md mx-auto bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                        <p className="font-medium">Permission Required</p>
                        <p className="mt-1">Your role ({userRole}) does not have permission to initiate scans. Contact an admin to request editor or admin access.</p>
                      </div>
                    )}

                    <div className="flex gap-3 justify-center">
                      {/* Show Resume button if partial scan exists */}
                      {partialScanProgress && (
                        <button
                          onClick={() => startScan()}
                          disabled={!hasPermission('scan') || (sources.length === 0 && selectedDocIds.length === 0)}
                          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                          title={!hasPermission('scan') ? `${userRole}s cannot initiate scans` : ''}
                        >
                          <RefreshCw className="h-5 w-5 mr-2" />
                          Resume Scan (from question {partialScanProgress.questionsProcessed + 1})
                        </button>
                      )}

                      {/* Show Start/Restart button */}
                      <button
                        onClick={() => startScan(0, true)}
                        disabled={!hasPermission('scan') || (sources.length === 0 && selectedDocIds.length === 0)}
                        className={`px-6 py-3 ${partialScanProgress ? 'bg-gray-600 hover:bg-gray-700' : 'bg-primary-600 hover:bg-primary-700'} text-white rounded-lg font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center`}
                        title={!hasPermission('scan') ? `${userRole}s cannot initiate scans` : ''}
                      >
                        <Search className="h-5 w-5 mr-2" />
                        {partialScanProgress ? 'Restart from Beginning' : 'Start Scan'}
                      </button>

                      {/* Show Clear Progress button if partial scan exists */}
                      {partialScanProgress && (
                        <button
                          onClick={clearProgress}
                          disabled={!hasPermission('scan')}
                          className="px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-lg inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                          title={!hasPermission('scan') ? `${userRole}s cannot clear progress` : ''}
                        >
                          <X className="h-5 w-5 mr-2" />
                          Clear Progress
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {!scanning && sources.length === 0 && selectedDocIds.length === 0 && (
                  <p className="text-sm text-gray-500 mt-4">
                    Please add web sources or select documents in the Configure tab first
                  </p>
                )}
              </div>
            )}

            {/* Review Tab */}
            {caiqSubTab === 'review' && (
              <div className="space-y-6" data-tour="kb-caiq-review">
                {!scanResults || scanResults.answers.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Results</h3>
                    <p className="text-gray-600 mb-6">Run a scan to find CAIQ answers from your sources</p>
                    <button
                      onClick={() => setActiveTab('scan')}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      Go to Scan
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-900">
                            Found {scanResults.answers.length} answers from {scanResults.documentCount} sources
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            Scanned at {new Date(scanResults.scannedAt).toLocaleString()}
                          </p>
                        </div>
                        {scanResults.answers.filter((a: any) => a.confidence > 0.8).length > 0 && (
                          <button
                            onClick={approveAllHighConfidence}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium flex items-center"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve All High Confidence ({scanResults.answers.filter((a: any) => a.confidence > 0.8).length})
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Filters */}
                    <div className="flex items-center gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700">Confidence:</label>
                        <select
                          value={confidenceFilter}
                          onChange={(e) => setConfidenceFilter(e.target.value as any)}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="all">All Levels</option>
                          <option value="high">High (&gt;80%)</option>
                          <option value="medium">Medium (50-80%)</option>
                          <option value="low">Low (&lt;50%)</option>
                        </select>
                      </div>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showConflictsOnly}
                          onChange={(e) => setShowConflictsOnly(e.target.checked)}
                          className="h-4 w-4 text-primary-600"
                        />
                        <span className="ml-2 text-sm text-gray-700">Show conflicts only</span>
                      </label>

                      <div className="ml-auto text-sm text-gray-600">
                        Showing {getFilteredAnswers().length} of {scanResults.answers.length} answers
                      </div>
                    </div>

                    {/* Bulk Actions Bar */}
                    {getFilteredAnswers().length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedAnswers.size > 0 && selectedAnswers.size === getFilteredAnswers().length}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedAnswers(new Set(getFilteredAnswers().map((a: any) => a.questionId)))
                                  } else {
                                    setSelectedAnswers(new Set())
                                  }
                                }}
                                className="h-4 w-4 text-primary-600"
                              />
                              <span className="ml-2 text-sm font-medium text-gray-700">
                                Select All
                              </span>
                            </label>
                            {selectedAnswers.size > 0 && (
                              <span className="text-sm text-gray-600">
                                {selectedAnswers.size} selected
                              </span>
                            )}
                          </div>
                          {selectedAnswers.size > 0 && (
                            <div className="flex gap-2">
                              <button
                                onClick={approveBulkSelected}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium flex items-center"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Approve Selected ({selectedAnswers.size})
                              </button>
                              <button
                                onClick={() => {
                                  const remaining = scanResults.answers.filter((a: any) => !selectedAnswers.has(a.questionId))
                                  setScanResults({ ...scanResults, answers: remaining })
                                  setSelectedAnswers(new Set())
                                }}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium flex items-center"
                              >
                                <X className="h-4 w-4 mr-2" />
                                Reject Selected ({selectedAnswers.size})
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="space-y-4">
                      {getFilteredAnswers().map((answer, index) => (
                        <div key={index} className={`border rounded-lg p-6 ${answer.conflicts ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200 bg-white'}`}>
                          <div className="flex items-start gap-3 mb-4">
                            <input
                              type="checkbox"
                              checked={selectedAnswers.has(answer.questionId)}
                              onChange={(e) => {
                                const newSelected = new Set(selectedAnswers)
                                if (e.target.checked) {
                                  newSelected.add(answer.questionId)
                                } else {
                                  newSelected.delete(answer.questionId)
                                }
                                setSelectedAnswers(newSelected)
                              }}
                              className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0"
                            />
                            <div className="flex-1 flex items-start justify-between">
                              <div>
                                <h4 className="text-lg font-semibold text-gray-900">
                                  {answer.questionId}: {CAIQ_V4.find(d => d.id === answer.domain)?.questions.find(q => q.id === answer.questionId)?.question || 'Question'}
                                </h4>
                                <p className="text-sm text-gray-600 mt-1">Domain: {answer.domain}</p>
                              </div>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getConfidenceColor(answer.confidence)}`}>
                                {getConfidenceLabel(answer.confidence)}
                              </span>
                            </div>
                          </div>

                          {answer.conflicts && (
                            <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-3 mb-4">
                              <div className="flex items-center">
                                <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                                <p className="text-sm font-medium text-yellow-900">Conflict Detected</p>
                              </div>
                              <p className="text-xs text-yellow-700 mt-1">{answer.conflictDetails}</p>
                            </div>
                          )}

                          <div className="space-y-3">
                            <div>
                              <p className="text-sm font-medium text-gray-700">Answer:</p>
                              <p className="text-base text-gray-900 mt-1 capitalize">{answer.answer}</p>
                            </div>

                            <div>
                              <p className="text-sm font-medium text-gray-700">Explanation:</p>
                              <p className="text-base text-gray-700 mt-1">{answer.explanation}</p>
                            </div>

                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">Sources:</p>
                              <div className="space-y-2">
                                {answer.sources?.map((source: any, idx: number) => (
                                  <div key={idx} className="bg-gray-50 border border-gray-200 rounded p-3">
                                    <div className="flex items-center justify-between mb-1">
                                      <p className="text-xs font-medium text-gray-900">{source.source}</p>
                                      <p className="text-xs text-gray-500">{new Date(source.date).toLocaleDateString()}</p>
                                    </div>
                                    <p className="text-xs text-gray-600">{source.excerpt}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                            <button
                              onClick={() => approveAnswer(answer)}
                              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center"
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Approve & Save
                            </button>
                            <button
                              onClick={() => openEditModal(answer)}
                              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => rejectAnswer(answer)}
                              className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 border border-red-200"
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Browse Tab */}
            {caiqSubTab === 'browse' && (
              <div className="space-y-6" data-tour="kb-caiq-browse">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">CAIQ v{CAIQ_VERSION} Domains</h3>
                    <p className="text-sm text-gray-600">
                      {TOTAL_QUESTIONS} questions across {CAIQ_V4.length} domains
                    </p>
                  </div>
                </div>

                {/* Export Actions */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">Export Knowledge Base</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Download answered questions in CSV or JSON format
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={exportToCSV}
                        disabled={Object.keys(savedAnswers).length === 0}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        <Download className="h-4 w-4" />
                        Export CSV
                      </button>
                      <button
                        onClick={exportToJSON}
                        disabled={Object.keys(savedAnswers).length === 0}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        <Download className="h-4 w-4" />
                        Export JSON
                      </button>
                    </div>
                  </div>
                </div>

                {/* Import Actions */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">Import Knowledge Base</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Upload CSV or JSON file to restore or migrate answers
                      </p>
                    </div>
                    <button
                      onClick={() => setShowImportModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      <Upload className="h-4 w-4" />
                      Import File
                    </button>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <strong>Export for CSA STAR:</strong> Download the CSV export and submit it to the{' '}
                    <a
                      href="https://cloudsecurityalliance.org/star/registry"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-blue-700"
                    >
                      CSA STAR Registry
                    </a>
                    {' '}to earn your STAR Level 1 certification.
                  </p>
                </div>

                {/* Search and Filters */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search questions by keyword or ID..."
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">Domain:</label>
                      <select
                        value={selectedDomain}
                        onChange={(e) => setSelectedDomain(e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="all">All Domains ({CAIQ_V4.length})</option>
                        {CAIQ_V4.map(d => (
                          <option key={d.id} value={d.id}>{d.name} ({d.questions.length})</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">Status:</label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as any)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="all">All Questions</option>
                        <option value="answered">Answered Only</option>
                        <option value="unanswered">Unanswered Only</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">Review Status:</label>
                      <select
                        value={reviewStatusFilter}
                        onChange={(e) => setReviewStatusFilter(e.target.value as any)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="all">All Statuses</option>
                        <option value="draft">Draft</option>
                        <option value="ai_draft">AI Draft</option>
                        <option value="under_review">Under Review</option>
                        <option value="approved">Approved</option>
                        <option value="needs_update">Needs Update</option>
                      </select>
                    </div>

                    <label className="flex items-center cursor-pointer ml-auto">
                      <input
                        type="checkbox"
                        checked={showMappings}
                        onChange={(e) => setShowMappings(e.target.checked)}
                        className="h-4 w-4 text-primary-600"
                      />
                      <span className="ml-2 text-sm text-gray-700">Show framework mappings</span>
                    </label>
                  </div>

                  {(searchQuery || selectedDomain !== 'all' || statusFilter !== 'all') && (
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <p className="text-sm text-gray-600">
                        Showing {getFilteredDomains().reduce((sum, d) => sum + d.questions.length, 0)} questions
                      </p>
                      <button
                        onClick={() => {
                          setSearchQuery('')
                          setSelectedDomain('all')
                          setStatusFilter('all')
                          setReviewStatusFilter('all')
                        }}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Clear filters
                      </button>
                    </div>
                  )}
                </div>

                {/* Bulk Approval Actions */}
                {Object.values(savedAnswers).some((a: any) => a.status !== 'approved') && (
                  <div className="bg-white border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <h3 className="font-semibold text-gray-900">Bulk Approval</h3>
                        {selectedAnswers.size > 0 && (
                          <span className="text-sm text-gray-600">
                            {selectedAnswers.size} answer{selectedAnswers.size !== 1 ? 's' : ''} selected
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {selectedAnswers.size === 0 ? (
                          <button
                            onClick={() => {
                              const nonApprovedAnswers = Object.values(savedAnswers).filter((a: any) => {
                                const matchesStatusFilter = reviewStatusFilter === 'all' || a.status === reviewStatusFilter
                                return a.status !== 'approved' && matchesStatusFilter
                              })
                              const allIds = new Set(nonApprovedAnswers.map((a: any) => a.questionId))
                              setSelectedAnswers(allIds)
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            <CheckCircle className="h-4 w-4" />
                            Select All {reviewStatusFilter === 'all' ? 'Non-Approved' : reviewStatusFilter === 'ai_draft' ? 'AI Drafts' : reviewStatusFilter === 'draft' ? 'Drafts' : reviewStatusFilter === 'under_review' ? 'Under Review' : reviewStatusFilter === 'needs_update' ? 'Needs Update' : reviewStatusFilter === 'approved' ? 'Approved' : 'Filtered'}
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={async () => {
                                if (!currentTenant || selectedAnswers.size === 0) return

                                const answersToApprove = Object.values(savedAnswers).filter((a: any) =>
                                  selectedAnswers.has(a.questionId)
                                )

                                if (answersToApprove.length === 0) return

                                try {
                                  const response = await fetch('/api/save-kb-answers', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                      tenantId: currentTenant.id,
                                      answers: answersToApprove.map((a: any) => ({ ...a, status: 'approved' })),
                                      replaceAll: false
                                    })
                                  })

                                  if (response.ok) {
                                    setSelectedAnswers(new Set())
                                    loadSavedKnowledgeBase()
                                  }
                                } catch (error) {
                                  console.error('Error bulk approving:', error)
                                }
                              }}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                              <CheckCircle className="h-4 w-4" />
                              Approve Selected ({selectedAnswers.size})
                            </button>
                            <button
                              onClick={() => setSelectedAnswers(new Set())}
                              className="flex items-center gap-2 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                            >
                              Clear Selection
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* AI Response Generation - Bulk Actions */}
                {statusFilter === 'unanswered' && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <h3 className="font-semibold text-gray-900">AI Response Generation</h3>
                        {selectedQuestionsForGen.size > 0 && (
                          <span className="text-sm text-gray-600">
                            {selectedQuestionsForGen.size} question{selectedQuestionsForGen.size !== 1 ? 's' : ''} selected
                          </span>
                        )}
                      </div>
                      {selectedQuestionsForGen.size > 0 && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => generateResponses('positive')}
                            disabled={generating || !hasApiKey}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            {generating ? (
                              <>
                                <Loader className="h-4 w-4 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="h-4 w-4" />
                                Generate Positive ({selectedQuestionsForGen.size})
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => generateResponses('negative')}
                            disabled={generating || !hasApiKey}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            {generating ? (
                              <>
                                <Loader className="h-4 w-4 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <X className="h-4 w-4" />
                                Generate Negative ({selectedQuestionsForGen.size})
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => setSelectedQuestionsForGen(new Set())}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                          >
                            Clear Selection
                          </button>
                        </div>
                      )}
                    </div>
                    {!hasApiKey && (
                      <div className="mt-3 text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <AlertCircle className="h-4 w-4 inline mr-2" />
                        API key required for AI generation. Add one in Settings.
                      </div>
                    )}
                  </div>
                )}

                {getFilteredDomains().length === 0 ? (
                  <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                    <Search className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No questions found</h3>
                    <p className="text-gray-600 mb-4">
                      Try adjusting your search or filter criteria
                    </p>
                    <button
                      onClick={() => {
                        setSearchQuery('')
                        setSelectedDomain('all')
                        setStatusFilter('all')
                      }}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      Clear all filters
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getFilteredDomains().map((domain) => {
                    const answeredInDomain = domain.questions.filter(q => savedAnswers[q.id]).length
                    const domainCompletion = (answeredInDomain / domain.questions.length) * 100

                    return (
                      <div key={domain.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <h4 className="font-semibold text-gray-900">{domain.name}</h4>
                            <div className="flex items-center gap-2">
                              <div className="w-32 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-green-600 h-2 rounded-full transition-all"
                                  style={{ width: `${domainCompletion}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-600">
                                {answeredInDomain}/{domain.questions.length}
                              </span>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{domain.description}</p>
                        <div className="space-y-2">
                          {domain.questions.map((q) => {
                            const answer = savedAnswers[q.id]
                            const isExpanded = expandedQuestions.has(q.id)

                            return (
                              <div key={q.id} className={`border rounded-lg ${answer ? (answer.status === 'ai_draft' ? 'border-blue-200 bg-blue-50' : 'border-green-200 bg-green-50') : 'border-gray-200'}`}>
                                <div className="flex items-start gap-3 p-3">
                                  {answer ? (
                                    answer.status !== 'approved' ? (
                                      <input
                                        type="checkbox"
                                        checked={selectedAnswers.has(answer.questionId)}
                                        onChange={(e) => {
                                          const newSelected = new Set(selectedAnswers)
                                          if (e.target.checked) {
                                            newSelected.add(answer.questionId)
                                          } else {
                                            newSelected.delete(answer.questionId)
                                          }
                                          setSelectedAnswers(newSelected)
                                        }}
                                        className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5"
                                      />
                                    ) : (
                                      <CheckCircle className={`h-5 w-5 ${answer.status === 'ai_draft' ? 'text-blue-600' : 'text-green-600'} flex-shrink-0 mt-0.5`} />
                                    )
                                  ) : (
                                    statusFilter === 'unanswered' ? (
                                      <input
                                        type="checkbox"
                                        checked={selectedQuestionsForGen.has(q.id)}
                                        onChange={(e) => {
                                          const newSelected = new Set(selectedQuestionsForGen)
                                          if (e.target.checked) {
                                            newSelected.add(q.id)
                                          } else {
                                            newSelected.delete(q.id)
                                          }
                                          setSelectedQuestionsForGen(newSelected)
                                        }}
                                        className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5"
                                      />
                                    ) : (
                                      <div className="h-5 w-5 rounded-full border-2 border-gray-300 flex-shrink-0 mt-0.5" />
                                    )
                                  )}
                                  <button
                                    onClick={() => answer ? toggleQuestion(q.id) : null}
                                    className="flex-1 text-left hover:bg-gray-50 transition-colors"
                                  >
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-medium text-gray-700">{q.id}:</span>
                                      <span className="text-gray-900">{q.question}</span>
                                      {answer?.status === 'ai_draft' && (
                                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded border border-blue-200 font-medium">
                                          AI Draft
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                  {answer ? (
                                    <button
                                      onClick={() => toggleQuestion(q.id)}
                                      className={`px-2 py-1 text-white text-xs rounded flex-shrink-0 ${answer.status === 'ai_draft' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}
                                    >
                                      {isExpanded ? '−' : '+'}
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => openManualEntry(q, domain.name)}
                                      className="px-3 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 flex items-center gap-1 flex-shrink-0"
                                      title="Add answer"
                                    >
                                      <Plus className="h-3 w-3" />
                                      Add
                                    </button>
                                  )}
                                </div>

                                {showMappings && q.mapsTo && (
                                  <div className="px-3 pb-2 pt-1">
                                    <div className="flex flex-wrap gap-1">
                                      {q.mapsTo.soc2 && q.mapsTo.soc2.map((control: string, idx: number) => (
                                        <span key={`soc2-${idx}`} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium">
                                          SOC2: {control}
                                        </span>
                                      ))}
                                      {q.mapsTo.iso27001 && q.mapsTo.iso27001.map((control: string, idx: number) => (
                                        <span key={`iso-${idx}`} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-medium">
                                          ISO27001: {control}
                                        </span>
                                      ))}
                                      {q.mapsTo.nist && q.mapsTo.nist.map((control: string, idx: number) => (
                                        <span key={`nist-${idx}`} className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium">
                                          NIST: {control}
                                        </span>
                                      ))}
                                      {q.mapsTo.gdpr && q.mapsTo.gdpr.map((control: string, idx: number) => (
                                        <span key={`gdpr-${idx}`} className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded font-medium">
                                          GDPR: {control}
                                        </span>
                                      ))}
                                      {q.mapsTo.pci && q.mapsTo.pci.map((control: string, idx: number) => (
                                        <span key={`pci-${idx}`} className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded font-medium">
                                          PCI: {control}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {isExpanded && answer && (
                                  <div className="px-3 pb-3 space-y-2 border-t border-green-200">
                                    <div className="mt-2 flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div>
                                          <p className="text-xs font-medium text-gray-700 mb-1">Status:</p>
                                          <select
                                            value={answer.status || 'draft'}
                                            onChange={(e) => updateAnswerStatus(q.id, e.target.value)}
                                            className={`px-2 py-1 rounded text-xs border font-medium ${getStatusBadge(answer.status).color}`}
                                          >
                                            <option value="draft">Draft</option>
                                            <option value="ai_draft">AI Draft</option>
                                            <option value="under_review">Under Review</option>
                                            <option value="approved">Approved</option>
                                            <option value="needs_update">Needs Update</option>
                                          </select>
                                        </div>
                                        {answer.generatedBy === 'AI' && (
                                          <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded border border-blue-200 font-medium">
                                            🤖 AI Generated
                                          </span>
                                        )}
                                      </div>
                                      {answer.lastReviewedAt && (
                                        <div className="text-right">
                                          <p className="text-xs text-gray-500">
                                            Reviewed: {new Date(answer.lastReviewedAt).toLocaleDateString()}
                                          </p>
                                          {answer.lastReviewedBy && (
                                            <p className="text-xs text-gray-500">By: {answer.lastReviewedBy}</p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <div className="mt-2">
                                      <p className="text-xs font-medium text-gray-700">Answer:</p>
                                      <p className="text-sm text-gray-900 capitalize mt-1">{answer.answer}</p>
                                    </div>
                                    {answer.explanation && (
                                      <div>
                                        <p className="text-xs font-medium text-gray-700">Explanation:</p>
                                        <p className="text-sm text-gray-700 mt-1">{answer.explanation}</p>
                                      </div>
                                    )}
                                    {answer.sources && answer.sources.length > 0 && (
                                      <div>
                                        <p className="text-xs font-medium text-gray-700 mb-1">Sources:</p>
                                        {answer.sources.map((source: any, idx: number) => (
                                          <div key={idx} className="text-xs text-gray-600 pl-2 border-l-2 border-gray-300">
                                            {source.source} ({new Date(source.date).toLocaleDateString()})
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {answer.lastUpdated && (
                                      <p className="text-xs text-gray-500 mt-2">
                                        Last updated: {new Date(answer.lastUpdated).toLocaleString()}
                                      </p>
                                    )}
                                    <div className="pt-3 border-t border-green-200 space-y-2">
                                      <div className="flex gap-2 flex-wrap">
                                        <button
                                          onClick={() => updateAnswerStatus(q.id, 'approved')}
                                          disabled={(answer.status || 'draft') === 'approved'}
                                          className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                        >
                                          ✓ Approve
                                        </button>
                                        <button
                                          onClick={() => updateAnswerStatus(q.id, 'under_review')}
                                          disabled={(answer.status || 'draft') === 'under_review'}
                                          className="px-3 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                        >
                                          ⏱ Mark for Review
                                        </button>
                                        <button
                                          onClick={() => updateAnswerStatus(q.id, 'needs_update')}
                                          disabled={(answer.status || 'draft') === 'needs_update'}
                                          className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                        >
                                          ⚠ Flag for Update
                                        </button>
                                        <button
                                          onClick={() => {
                                            setEditingAnswer({
                                              questionId: q.id,
                                              domain: domain.name,
                                              ...answer
                                            })
                                            toggleQuestion(q.id)
                                          }}
                                          className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                                        >
                                          ✎ Edit
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                  </div>
                )}
              </div>
            )}

            {/* Analytics Tab */}
            {caiqSubTab === 'analytics' && (
              <div className="space-y-6" data-tour="kb-caiq-analytics">
                <h3 className="text-lg font-semibold text-gray-900">Knowledge Base Analytics</h3>

                {/* Completion Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-900">Total Completion</p>
                        <p className="text-3xl font-bold text-blue-700 mt-2">{completion}%</p>
                      </div>
                      <CheckCircle className="h-12 w-12 text-blue-600 opacity-20" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-900">Answered</p>
                        <p className="text-3xl font-bold text-green-700 mt-2">{Object.keys(savedAnswers).length}</p>
                        <p className="text-xs text-green-600 mt-1">of {TOTAL_QUESTIONS} questions</p>
                      </div>
                      <CheckCircle className="h-12 w-12 text-green-600 opacity-20" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-orange-900">Remaining</p>
                        <p className="text-3xl font-bold text-orange-700 mt-2">{TOTAL_QUESTIONS - Object.keys(savedAnswers).length}</p>
                        <p className="text-xs text-orange-600 mt-1">questions to answer</p>
                      </div>
                      <AlertCircle className="h-12 w-12 text-orange-600 opacity-20" />
                    </div>
                  </div>
                </div>

                {/* Domain Breakdown */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Completion by Domain</h4>
                  <div className="space-y-4">
                    {CAIQ_V4.map((domain) => {
                      const answeredInDomain = domain.questions.filter(q => savedAnswers[q.id]).length
                      const domainCompletion = (answeredInDomain / domain.questions.length) * 100

                      return (
                        <div key={domain.id}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{domain.name}</span>
                              <span className="text-xs text-gray-500">({domain.id})</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-gray-600">{answeredInDomain}/{domain.questions.length}</span>
                              <span className="text-sm font-semibold text-gray-900 min-w-[45px] text-right">
                                {Math.round(domainCompletion)}%
                              </span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all ${
                                domainCompletion === 100 ? 'bg-green-600' :
                                domainCompletion >= 50 ? 'bg-blue-600' :
                                domainCompletion > 0 ? 'bg-yellow-500' :
                                'bg-gray-300'
                              }`}
                              style={{ width: `${domainCompletion}%` }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Recent Updates</h4>
                  {Object.keys(savedAnswers).length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">No answers yet</p>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(savedAnswers)
                        .sort((a, b) => {
                          const timeA = new Date(a[1].lastUpdated || 0).getTime()
                          const timeB = new Date(b[1].lastUpdated || 0).getTime()
                          return timeB - timeA
                        })
                        .slice(0, 10)
                        .map(([questionId, answer]) => {
                          const domain = CAIQ_V4.find(d => d.questions.some(q => q.id === questionId))
                          const question = domain?.questions.find(q => q.id === questionId)

                          return (
                            <div key={questionId} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{questionId}</p>
                                <p className="text-xs text-gray-600 mt-1 truncate">{question?.question}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <span className="text-xs text-gray-500">{domain?.name}</span>
                                  {answer.lastUpdated && (
                                    <>
                                      <span className="text-xs text-gray-400">•</span>
                                      <span className="text-xs text-gray-500">
                                        {new Date(answer.lastUpdated).toLocaleDateString()}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                    </div>
                  )}
                </div>

                {/* Review Status Distribution */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Review Status Distribution</h4>
                  {Object.keys(savedAnswers).length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">No answers yet</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {[
                        { status: 'draft', label: 'Draft', color: 'bg-gray-50 border-gray-200 text-gray-900', textBold: 'text-gray-700', textSmall: 'text-gray-600' },
                        { status: 'under_review', label: 'Under Review', color: 'bg-yellow-50 border-yellow-200 text-yellow-900', textBold: 'text-yellow-700', textSmall: 'text-yellow-600' },
                        { status: 'approved', label: 'Approved', color: 'bg-green-50 border-green-200 text-green-900', textBold: 'text-green-700', textSmall: 'text-green-600' },
                        { status: 'needs_update', label: 'Needs Update', color: 'bg-red-50 border-red-200 text-red-900', textBold: 'text-red-700', textSmall: 'text-red-600' }
                      ].map((statusInfo) => {
                        const count = Object.values(savedAnswers).filter((answer: any) =>
                          (answer.status || 'draft') === statusInfo.status
                        ).length

                        const percentage = Object.keys(savedAnswers).length > 0
                          ? Math.round((count / Object.keys(savedAnswers).length) * 100)
                          : 0

                        return (
                          <div key={statusInfo.status} className={`${statusInfo.color} border rounded-lg p-4`}>
                            <p className="text-sm font-medium">{statusInfo.label}</p>
                            <p className={`text-2xl font-bold ${statusInfo.textBold} mt-2`}>{count}</p>
                            <p className={`text-xs ${statusInfo.textSmall} mt-1`}>{percentage}% of total</p>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Confidence Distribution */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Confidence Distribution</h4>
                  {Object.keys(savedAnswers).length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">No answers yet</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {[
                        { label: 'High (>80%)', min: 0.8, color: 'green' },
                        { label: 'Medium (50-80%)', min: 0.5, max: 0.8, color: 'yellow' },
                        { label: 'Low (<50%)', max: 0.5, color: 'red' }
                      ].map((level) => {
                        const count = Object.values(savedAnswers).filter((answer: any) => {
                          const conf = answer.confidence || 1.0
                          if (level.max === undefined) return conf >= level.min
                          if (level.min === undefined) return conf < level.max
                          return conf >= level.min && conf < level.max
                        }).length

                        const percentage = Object.keys(savedAnswers).length > 0
                          ? Math.round((count / Object.keys(savedAnswers).length) * 100)
                          : 0

                        const colorClasses = {
                          green: 'bg-green-50 border-green-200 text-green-900',
                          yellow: 'bg-yellow-50 border-yellow-200 text-yellow-900',
                          red: 'bg-red-50 border-red-200 text-red-900'
                        }[level.color]

                        const textBoldClasses = {
                          green: 'text-green-700',
                          yellow: 'text-yellow-700',
                          red: 'text-red-700'
                        }[level.color]

                        const textSmallClasses = {
                          green: 'text-green-600',
                          yellow: 'text-yellow-600',
                          red: 'text-red-600'
                        }[level.color]

                        return (
                          <div key={level.label} className={`${colorClasses} border rounded-lg p-4`}>
                            <p className="text-sm font-medium">{level.label}</p>
                            <p className={`text-2xl font-bold ${textBoldClasses} mt-2`}>{count}</p>
                            <p className={`text-xs ${textSmallClasses} mt-1`}>{percentage}% of total</p>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Manual Entry Modal */}
      {manualEntryQuestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Add Answer</h3>
                <button
                  onClick={() => setManualEntryQuestion(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-900">{manualEntryQuestion.id}</p>
                <p className="text-sm text-blue-800 mt-1">{manualEntryQuestion.question}</p>
                <p className="text-xs text-blue-600 mt-2">Domain: {manualEntryQuestion.domain}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                <select
                  value={manualAnswer.answer}
                  onChange={(e) => setManualAnswer({ ...manualAnswer, answer: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="unclear">Unclear</option>
                  <option value="text">Text Response</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Explanation</label>
                <textarea
                  value={manualAnswer.explanation}
                  onChange={(e) => setManualAnswer({ ...manualAnswer, explanation: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Provide a brief explanation of the answer..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confidence: {Math.round(manualAnswer.confidence * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={manualAnswer.confidence * 100}
                  onChange={(e) => setManualAnswer({ ...manualAnswer, confidence: parseInt(e.target.value) / 100 })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Low</span>
                  <span>Medium</span>
                  <span>High</span>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-600">
                  <strong>Tip:</strong> Manual answers are saved with 100% confidence by default.
                  Adjust the slider if you're uncertain about the accuracy.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setManualEntryQuestion(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveManualAnswer}
                disabled={!manualAnswer.explanation.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Save Answer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Answer Modal */}
      {editingAnswer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Edit Answer</h3>
                <button
                  onClick={closeEditModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Question ID</label>
                <input
                  type="text"
                  value={editingAnswer.questionId}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                <select
                  value={editingAnswer.answer}
                  onChange={(e) => setEditingAnswer({ ...editingAnswer, answer: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="unclear">Unclear</option>
                  <option value="text">Text Response</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Explanation</label>
                <textarea
                  value={editingAnswer.explanation}
                  onChange={(e) => setEditingAnswer({ ...editingAnswer, explanation: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Provide a brief explanation of the answer..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confidence: {Math.round(editingAnswer.confidence * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={editingAnswer.confidence * 100}
                  onChange={(e) => setEditingAnswer({ ...editingAnswer, confidence: parseInt(e.target.value) / 100 })}
                  className="w-full"
                />
              </div>

              {editingAnswer.conflicts && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-yellow-900">⚠️ This answer has conflicts</p>
                  <p className="text-xs text-yellow-700 mt-1">{editingAnswer.conflictDetails}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeEditModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveEditedAnswer}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Save & Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discovery Modal */}
      {showDiscoveryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Discovered Security URLs</h3>
                <button
                  onClick={() => setShowDiscoveryModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Found {discoveredUrls.length} security-related URLs for {companyDomain}
              </p>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {discoveredUrls.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">No security URLs found</p>
                  <p className="text-xs text-gray-500 mt-1">Try adding URLs manually or check the domain</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {discoveredUrls.map((url, idx) => (
                    <label key={idx} className="flex items-start p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        defaultChecked={true}
                        className="h-4 w-4 text-primary-600 mt-1"
                        id={`url-${idx}`}
                      />
                      <div className="ml-3 flex-1">
                        <p className="text-sm font-medium text-gray-900">{url.title}</p>
                        <p className="text-xs text-gray-500 mt-1">{url.url}</p>
                        <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                          {url.type}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowDiscoveryModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const selectedUrls = discoveredUrls
                    .filter((_, idx) => {
                      const checkbox = document.getElementById(`url-${idx}`) as HTMLInputElement
                      return checkbox?.checked
                    })
                    .map(url => url.url)
                  addDiscoveredUrls(selectedUrls)
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Add Selected URLs
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-full sm:max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Import Knowledge Base</h3>
                <button
                  onClick={() => {
                    setShowImportModal(false)
                    setImportFile(null)
                    setImportPreview([])
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload File (CSV or JSON)
                </label>
                <input
                  type="file"
                  accept=".csv,.json"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleImportFile(file)
                  }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
              </div>

              {/* Strategy Selection */}
              {importPreview.length > 0 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Import Strategy
                    </label>
                    <select
                      value={importStrategy}
                      onChange={(e) => setImportStrategy(e.target.value as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="merge">Merge (Keep existing, add new)</option>
                      <option value="update">Update (Overwrite existing with new)</option>
                      <option value="replace">Replace All (Delete all, import new)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {importStrategy === 'merge' && 'Existing answers will be preserved. Only new questions will be added.'}
                      {importStrategy === 'update' && 'Existing answers will be updated with imported data. New questions will be added.'}
                      {importStrategy === 'replace' && 'All existing answers will be deleted and replaced with imported data.'}
                    </p>
                  </div>

                  {/* Preview */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Preview ({importPreview.length} answers)
                    </h4>
                    <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Question ID</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Domain</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Answer</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Confidence</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {importPreview.slice(0, 10).map((item, idx) => (
                            <tr key={idx}>
                              <td className="px-4 py-2 text-sm text-gray-900">{item.questionId}</td>
                              <td className="px-4 py-2 text-sm text-gray-600">{item.domain}</td>
                              <td className="px-4 py-2 text-sm text-gray-600 capitalize">{item.answer}</td>
                              <td className="px-4 py-2 text-sm text-gray-600">{Math.round((item.confidence || 1) * 100)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {importPreview.length > 10 && (
                        <div className="p-2 text-center text-xs text-gray-500 bg-gray-50">
                          ... and {importPreview.length - 10} more
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Import Button */}
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={executeImport}
                      disabled={importing}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {importing ? (
                        <>
                          <Loader className="h-4 w-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Import {importPreview.length} Answers
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowImportModal(false)
                        setImportFile(null)
                        setImportPreview([])
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}

              {importFile && importPreview.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Loader className="h-8 w-8 mx-auto mb-2 animate-spin" />
                  <p>Parsing file...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lock Error Modal */}
      {lockError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0">
                <AlertCircle className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Answer Locked</h3>
                <p className="text-sm text-gray-600">{lockError}</p>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setLockError(null)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Log Panel */}
      {showActivityLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-3xl w-full max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <Clock className="h-6 w-6 text-gray-700 mr-2" />
                <h2 className="text-xl font-bold text-gray-900">Activity Log</h2>
              </div>
              <button
                onClick={() => setShowActivityLog(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Activity List */}
            <div className="flex-1 overflow-y-auto p-6">
              {activityLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No activity yet</p>
                  <p className="text-xs mt-1">User actions will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {activityLogs.map((log: any, idx: number) => {
                    const actionDetails = getActionDetails(log.action)
                    const ActionIcon = actionDetails.icon

                    return (
                      <div
                        key={log.id || idx}
                        className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className={`flex-shrink-0 mt-1 ${actionDetails.color}`}>
                          <ActionIcon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2 mb-1">
                            <p className="text-sm font-medium text-gray-900">
                              {log.performedByName || log.performedBy}
                              <span className="font-normal text-gray-600 ml-1">{actionDetails.label}</span>
                            </p>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {formatTimestamp(log.timestamp)}
                            </span>
                          </div>
                          {log.metadata && (
                            <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                              {log.metadata.questionsProcessed !== undefined && (
                                <p>Questions: {log.metadata.questionsProcessed} of {log.metadata.totalQuestions || TOTAL_QUESTIONS}</p>
                              )}
                              {log.metadata.answersFound !== undefined && (
                                <p>Answers found: {log.metadata.answersFound}</p>
                              )}
                              {log.metadata.errorMessage && (
                                <p className="text-red-600">Error: {log.metadata.errorMessage}</p>
                              )}
                              {log.metadata.answerIds && log.metadata.answerIds.length > 0 && (
                                <p>Affected {log.metadata.answerIds.length} answer{log.metadata.answerIds.length > 1 ? 's' : ''}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-500">
                Showing {activityLogs.length} recent activit{activityLogs.length === 1 ? 'y' : 'ies'}
              </p>
              <button
                onClick={() => {
                  loadActivityLogs()
                  // Show a brief success message
                  const btn = document.activeElement as HTMLButtonElement
                  const originalText = btn?.textContent
                  if (btn) {
                    btn.textContent = '✓ Refreshed'
                    setTimeout(() => {
                      btn.textContent = originalText || 'Refresh'
                    }, 1500)
                  }
                }}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Questionnaire Generate Prompt Modal */}
      {showGeneratePrompt && questionnaireUnknowns.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-full sm:max-w-2xl w-full p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {questionnaireUnknowns.length} Questions Not Found in Knowledge Base
            </h2>
            <p className="text-gray-700 mb-4">
              {questionnaireAnswers.length > 0 && `We found answers for ${questionnaireAnswers.length} questions in your knowledge base. `}
              For the remaining {questionnaireUnknowns.length} questions, would you like to generate positive or negative responses?
            </p>

            <div className="space-y-3 mb-6">
              <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                style={{ borderColor: selectedResponseType === 'positive' ? '#2563eb' : '#e5e7eb' }}>
                <input
                  type="radio"
                  name="responseType"
                  value="positive"
                  checked={selectedResponseType === 'positive'}
                  onChange={() => setSelectedResponseType('positive')}
                  className="mt-1 h-4 w-4 text-primary-600"
                />
                <div className="ml-3">
                  <div className="font-medium text-gray-900">Generate Positive Responses</div>
                  <div className="text-sm text-gray-600">AI will generate affirmative answers demonstrating strong security controls and compliance</div>
                </div>
              </label>

              <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                style={{ borderColor: selectedResponseType === 'negative' ? '#2563eb' : '#e5e7eb' }}>
                <input
                  type="radio"
                  name="responseType"
                  value="negative"
                  checked={selectedResponseType === 'negative'}
                  onChange={() => setSelectedResponseType('negative')}
                  className="mt-1 h-4 w-4 text-primary-600"
                />
                <div className="ml-3">
                  <div className="font-medium text-gray-900">Generate Negative Responses</div>
                  <div className="text-sm text-gray-600">AI will generate responses indicating these controls are not currently implemented</div>
                </div>
              </label>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowGeneratePrompt(false)
                  setQuestionnaireUnknowns([])
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Skip These Questions
              </button>
              <button
                onClick={handleGenerateForUnknowns}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center"
              >
                <Zap className="h-4 w-4 mr-2" />
                Generate {selectedResponseType === 'positive' ? 'Positive' : 'Negative'} Responses
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generated Responses Review Modal */}
      {showGeneratedReview && generatedResponses.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-full sm:max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Review Generated Responses</h2>
                <button
                  onClick={() => {
                    setShowGeneratedReview(false)
                    setGeneratedResponses([])
                    setSelectedGeneratedForApproval(new Set())
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Bulk Actions */}
              <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedGeneratedForApproval.size > 0 && selectedGeneratedForApproval.size === generatedResponses.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedGeneratedForApproval(new Set(generatedResponses.map((_, i) => i)))
                        } else {
                          setSelectedGeneratedForApproval(new Set())
                        }
                      }}
                      className="h-4 w-4 text-primary-600"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Select All ({generatedResponses.length})
                    </span>
                  </label>
                  {selectedGeneratedForApproval.size > 0 && (
                    <span className="text-sm text-gray-600">
                      {selectedGeneratedForApproval.size} selected
                    </span>
                  )}
                </div>
                {selectedGeneratedForApproval.size > 0 && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => approveGeneratedResponses(Array.from(selectedGeneratedForApproval))}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Approve ({selectedGeneratedForApproval.size})
                    </button>
                    <button
                      onClick={() => rejectGeneratedResponses(Array.from(selectedGeneratedForApproval))}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                    >
                      <X className="h-4 w-4" />
                      Reject ({selectedGeneratedForApproval.size})
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 space-y-4">
              {generatedResponses.map((response, index) => {
                const question = CAIQ_V4.flatMap(d => d.questions).find(q => q.id === response.questionId)
                return (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="flex items-start gap-3 mb-4">
                      <input
                        type="checkbox"
                        checked={selectedGeneratedForApproval.has(index)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedGeneratedForApproval)
                          if (e.target.checked) {
                            newSelected.add(index)
                          } else {
                            newSelected.delete(index)
                          }
                          setSelectedGeneratedForApproval(newSelected)
                        }}
                        className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0"
                      />
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-900 mb-1">
                          {response.questionId}: {question?.question}
                        </h4>
                        <p className="text-sm text-gray-600">Domain: {response.domain}</p>
                      </div>
                    </div>

                    <div className="ml-8 space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Answer:</p>
                        <p className="text-base text-gray-900 mt-1 capitalize">{response.answer}</p>
                      </div>

                      <div>
                        <p className="text-sm font-medium text-gray-700">Explanation:</p>
                        <p className="text-base text-gray-700 mt-1">{response.explanation}</p>
                      </div>

                      <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                        <span className="px-3 py-1 rounded-full text-xs font-medium border bg-blue-50 border-blue-200 text-blue-700">
                          AI Generated
                        </span>
                        <span className="text-xs text-gray-500">
                          Use checkbox to select, then use buttons at top to approve/reject
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

            {/* TPRM Tab */}
            {activeTab === 'tprm' && featureFlags.tprm && (
              <div className="space-y-6">
                {/* Tab Explanation */}
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Database className="h-6 w-6 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">TPRM (Third-Party Risk Management)</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        Comprehensive vendor risk assessment and contract lifecycle management powered by AI extraction and analysis.
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 mt-1">•</span>
                          <span><strong>Dashboard:</strong> Overview of vendor risk metrics, contract status, and compliance</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 mt-1">•</span>
                          <span><strong>Assessments:</strong> Create and manage vendor security questionnaires and risk assessments</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 mt-1">•</span>
                          <span><strong>Insights:</strong> AI-generated risk analysis and recommendations for vendor relationships</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 mt-1">•</span>
                          <span><strong>Contracts:</strong> Upload, extract, and track key terms from vendor contracts automatically</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 mt-1">•</span>
                          <span><strong>Vendors:</strong> Centralized vendor directory with risk scores and compliance status</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* TPRM Sub-Tabs */}
                <div className="bg-white rounded-lg shadow-sm border-b border-gray-200">
                  <div className="flex space-x-8 px-6">
                    <button
                      onClick={() => setTprmSubTab('dashboard')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        tprmSubTab === 'dashboard'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      data-tour-subtab="tprm-dashboard"
                    >
                      Dashboard
                    </button>
                    <button
                      onClick={() => setTprmSubTab('assessments')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        tprmSubTab === 'assessments'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      data-tour-subtab="tprm-assessments"
                    >
                      Assessments
                    </button>
                    <button
                      onClick={() => setTprmSubTab('insights')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        tprmSubTab === 'insights'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      data-tour-subtab="tprm-insights"
                    >
                      Insights
                    </button>
                    <button
                      onClick={() => setTprmSubTab('processing')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        tprmSubTab === 'processing'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      data-tour-subtab="tprm-processing"
                    >
                      Contracts
                    </button>
                    <button
                      onClick={() => setTprmSubTab('vendors')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        tprmSubTab === 'vendors'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      data-tour-subtab="tprm-config"
                    >
                      Config
                    </button>
                  </div>
                </div>

                {/* Dashboard Sub-Tab */}
                {tprmSubTab === 'dashboard' && (
                  <div className="p-6" data-tour="tprm-dashboard">
                    <TPRMDashboard />
                  </div>
                )}

                {/* Assessments Sub-Tab */}
                {tprmSubTab === 'assessments' && (
                  <VendorRiskAssessment initialTab="assessments" />
                )}

                {/* Vendors Sub-Tab */}
                {tprmSubTab === 'vendors' && (
                  <VendorRiskAssessment initialTab="settings" />
                )}

                {/* Insights Sub-Tab */}
                {tprmSubTab === 'insights' && (
                  <ContractsManagement initialTab="insights" />
                )}

                {/* Processing Sub-Tab (Contracts) */}
                {tprmSubTab === 'processing' && (
                  <ContractsManagement initialTab="processing" />
                )}
              </div>
            )}
    </div>
  )
}

export default KnowledgeBasePage
